import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import SignInModal from "../components/SignInModal";
import ContactVendorleadsModal from "../components/ContactVendorleadsModal";
import Navbar from "../components/Navbar";
import FAQ from "../components/FAQ";
import TestimonialsShowcase from "../components/TestimonialsShowcase";
import { handleTokenAndPostRequest } from "../utils/utils";
import { agentSolidColour, agentColourAlpha } from "../utils/agentColour";

// --- Color Palette ---
const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const GRAY_DARK_COLOR = "#374151";
const WHITE_COLOR = "#fff";
const LIGHT_BG = "#f8fafc";
const DARK_GRADIENT = "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)";
const ACCENT_GRADIENT = "linear-gradient(135deg, #10b981, #3b82f6)";
const GRID_OVERLAY =
  "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)";

// =============================================================================
// Icon system: professional stroke icons (no emojis).
// =============================================================================
const ICON_PATHS = {
  home: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </>
  ),
  report: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </>
  ),
  mail: (
    <>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  message: (
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  ),
  article: (
    <>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </>
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  mapPin: (
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  appraisal: (
    <>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <polyline points="9 13 11 15 15 11" />
    </>
  ),
  messageReply: (
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  ),
  draft: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
    </>
  ),
  checkCircle: (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  send: (
    <>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </>
  ),
  brand: (
    <>
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </>
  ),
  sliders: (
    <>
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  trendingUp: (
    <>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </>
  ),
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </>
  ),
  key: (
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3" />
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </>
  ),
  sparkles: (
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
  ),
  arrowRight: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  lock: (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  loader: (
    <>
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </>
  ),
  spreadsheet: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </>
  ),
};

function Icon({ name, size = 24, color = "currentColor", strokeWidth = 2, style, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// Icon inside a tinted rounded tile: the shared visual unit across the page.
function IconTile({ name, size = 52, iconSize = 26, color = BRAND_ACCENT_COLOR, style }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size >= 48 ? "14px" : "10px",
        background: `${color}1A`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...style,
      }}
    >
      <Icon name={name} size={iconSize} color={color} />
    </div>
  );
}

// =============================================================================
// THE THREE JOBS: every feature lives inside one outcome, as supporting proof.
// =============================================================================
const JOBS = [
  {
    num: "1",
    eyebrow: "Grows your database",
    summary: "Grow your database",
    title: "Your branded insights hub, packed with assets that grow your database",
    subtitle: "",
    background: LIGHT_BG,
    features: [
      {
        icon: "home",
        title: "Self-Service Property Valuator",
        description:
          "Homeowners value their own property on your branded page. You collect the lead while they do the work.",
        to: "/features/diy-ai-property-valuator",
        demoUrl:
          "https://homeprice.site/property-profile/22-sevira-avenue-gledswood-hills-nsw-2557-vl3272ep?agent=demo-agency",
        branded: { kind: "link", label: "Demo" },
      },
      {
        icon: "qr",
        title: "QR Code Print Campaigns",
        description:
          "Drop a flyer, capture a lead. Scans get an instant report and join your ongoing updates. Set and forget.",
        to: "/features/qr-code-flyer",
      },
      {
        icon: "article",
        title: "AI Articles",
        description:
          "Data-driven real estate articles published under your brand, growing your reputation on autopilot.",
        to: "/features/ai-news-articles",
        demoUrl:
          "https://homeprice.site/news/liverpool-property-market-update-march-2026-prices-hold-firm-rental-demand-surges-despite-slower-sales-45?agent=demo-agency",
        branded: { kind: "link", label: "Demo" },
      },
    ],
  },
  {
    num: "2",
    eyebrow: "Keeps every contact warm",
    summary: "Keep contacts warm",
    title: "Your agent stays top of mind, month after month",
    background: WHITE_COLOR,
    features: [
      {
        icon: "report",
        title: "Monthly Property Reports",
        description:
          "Beautifully branded reports analysing comparable sales and values land in every inbox, every month.",
        to: "/features/monthly-ai-property-report",
        demoUrl: "https://homeprice.site/property-search?agent=demo-agency",
        branded: {
          kind: "send",
          channel: "email",
          endpoint: "send-property-report",
          reportType: "property_update",
          cta: "Send",
          placeholder: "you@email.com",
        },
      },
      {
        icon: "message",
        title: "AI SMS Campaigns",
        description:
          "Personalised property insights hit phones on a set schedule. Replies get an AI draft. You just approve.",
        to: "/features/sms-campaigns",
        branded: {
          kind: "send",
          channel: "sms",
          endpoint: "send-property-update-sms-sample",
          cta: "Send",
          placeholder: "0421 346 175",
        },
      },
    ],
  },
  {
    num: "3",
    eyebrow: "Spots the sellers & helps you win",
    summary: "Spot the sellers",
    title: "Your agent tells you exactly who's ready to sell",
    background: LIGHT_BG,
    features: [
      {
        icon: "mapPin",
        title: "Seller Signals on a Map",
        description:
          "Every analysis, open, click and download plotted on a map so you see where to focus at a glance.",
        to: "/features/map-prospector",
      },
      {
        icon: "activity",
        title: "Full Activity Timeline",
        description:
          "The same engagement as a searchable timeline. Filter by campaign, event or contact to spot intent.",
        to: "/features/map-prospector",
      },
      {
        icon: "appraisal",
        title: "Premium AI Appraisals",
        description:
          "Build a stunning, branded appraisal in minutes. AI scores comparables and publishes a shareable page.",
        to: "/features/property-appraisals",
        demoUrl: "https://www.homeprice.site/appraisal/apr_4e8bbb8ae8be",
      },
    ],
  },
];

// =============================================================================
// Floating step tabs: a pill that appears while the three JOB sections are on
// screen. The section currently in view expands to show its 2-3 word summary;
// the others collapse to just their number. The whole bar fades away once none
// of the three sections are in view. Clicking a tab scrolls to that section.
// =============================================================================
function JobTabs({ jobs, isMobile }) {
  const [activeNum, setActiveNum] = useState(null); // null → bar hidden

  useEffect(() => {
    const sections = jobs
      .map((j) => document.getElementById(`job-${j.num}`))
      .filter(Boolean);
    if (!sections.length) return undefined;

    // Track how many vertical pixels of each section are visible, and feature
    // whichever shows the most. When nothing is visible, hide the bar.
    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRect.height > 0) {
            visible.set(e.target.id, e.intersectionRect.height);
          } else {
            visible.delete(e.target.id);
          }
        });
        if (visible.size === 0) {
          setActiveNum(null);
          return;
        }
        let bestId = null;
        let bestH = -1;
        visible.forEach((h, id) => {
          if (h > bestH) {
            bestH = h;
            bestId = id;
          }
        });
        setActiveNum(bestId ? bestId.replace("job-", "") : null);
      },
      { threshold: [0, 0.15, 0.35, 0.6, 0.85, 1] }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [jobs]);

  const scrollTo = (num) => {
    const el = document.getElementById(`job-${num}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shown = activeNum != null;

  return (
    <div
      aria-hidden={!shown}
      style={{
        position: "fixed",
        top: isMobile ? 80 : 88,
        left: 0,
        right: 0,
        zIndex: 900,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        padding: "0 1rem",
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(-14px)",
        transition: "opacity 0.35s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div
        style={{
          pointerEvents: shown ? "auto" : "none",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 4 : 6,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid #e2e8f0",
          borderRadius: 999,
          boxShadow: "0 8px 28px rgba(15,23,42,0.14)",
          padding: isMobile ? "5px" : "6px",
        }}
      >
        {jobs.map((job) => {
          const active = job.num === activeNum;
          return (
            <button
              key={job.num}
              type="button"
              onClick={() => scrollTo(job.num)}
              aria-label={`Step ${job.num}: ${job.summary}`}
              aria-current={active}
              style={{
                appearance: "none",
                WebkitAppearance: "none",
                cursor: "pointer",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: active ? 8 : 0,
                background: active ? ACCENT_GRADIENT : "transparent",
                color: active ? WHITE_COLOR : "#64748b",
                borderRadius: 999,
                padding: active ? (isMobile ? "5px 12px 5px 6px" : "6px 16px 6px 6px") : "6px",
                transition: "gap 0.3s ease, background 0.3s ease, color 0.3s ease, padding 0.3s ease",
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: isMobile ? 24 : 28,
                  height: isMobile ? 24 : 28,
                  borderRadius: "50%",
                  background: active ? "rgba(255,255,255,0.22)" : "#f1f5f9",
                  color: active ? WHITE_COLOR : "#94a3b8",
                  fontWeight: 800,
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease, color 0.3s ease",
                }}
              >
                {job.num}
              </span>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  fontWeight: 700,
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  maxWidth: active ? (isMobile ? 150 : 200) : 0,
                  opacity: active ? 1 : 0,
                  transition: "max-width 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease",
                }}
              >
                {job.summary}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Hero "business card" that introduces the agent as a person. Kept available
// for reuse; the hero now leads with the live seller-signal map instead.
// =============================================================================
// eslint-disable-next-line no-unused-vars
function AgentCard({ isMobile, branding }) {
  return (
    <div
      style={{
        background: WHITE_COLOR,
        borderRadius: "20px",
        boxShadow: "0 24px 70px rgba(0,0,0,0.35), 0 6px 20px rgba(0,0,0,0.15)",
        border: "1px solid rgba(255,255,255,0.6)",
        padding: isMobile ? "1.5rem" : "1.75rem",
        maxWidth: "380px",
        width: "100%",
        margin: "0 auto",
      }}
    >
      {/* Header: avatar + name */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <AgentPhoto size={64} style={{ boxShadow: "0 0 24px rgba(16,185,129,0.35)" }} />
          <div
            style={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              background: "#10b981",
              border: "2px solid #fff",
              boxShadow: "0 0 8px rgba(16,185,129,0.8)",
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "1.15rem", fontWeight: 800, color: BRAND_COLOR }}>{ONBOARD_AGENT.name.split(" ")[0]}</span>
            <span
              style={{
                background: "rgba(16,185,129,0.12)",
                color: BRAND_ACCENT_COLOR,
                fontSize: "0.6rem",
                fontWeight: 700,
                padding: "0.1rem 0.4rem",
                borderRadius: "4px",
                letterSpacing: "0.05em",
              }}
            >
              AI AGENT
            </span>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 500 }}>{associateTitle(branding)}</div>
        </div>
      </div>

      {/* Inbox */}
      <div
        style={{
          marginTop: "1.1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          background: LIGHT_BG,
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          padding: "0.6rem 0.8rem",
        }}
      >
        <Icon name="mail" size={16} color="#64748b" />
        <span style={{ fontSize: "0.82rem", color: GRAY_DARK_COLOR, fontWeight: 600 }}>
          {ONBOARD_AGENT.email}
        </span>
      </div>

      {/* Phone */}
      <div
        style={{
          marginTop: "0.6rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          background: LIGHT_BG,
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          padding: "0.6rem 0.8rem",
        }}
      >
        <Icon name="phone" size={16} color="#64748b" />
        <span style={{ fontSize: "0.82rem", color: GRAY_DARK_COLOR, fontWeight: 600 }}>
          {ONBOARD_AGENT.phone}
        </span>
      </div>

      {/* Status */}
      <div style={{ marginTop: "0.6rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#10b981" }} />
        <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Online 24/7 · replies in seconds</span>
      </div>

      {/* Divider */}
      <div style={{ height: "1px", background: "#eef2f6", margin: "1.1rem 0" }} />

      {/* What this agent does */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {[
          { icon: "target", label: "Captures your leads" },
          { icon: "users", label: "Nurtures every contact" },
          { icon: "trendingUp", label: "Flags the hot sellers" },
        ].map((row) => (
          <div key={row.label} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <IconTile name={row.icon} size={26} iconSize={15} />
            <span style={{ fontSize: "0.85rem", color: GRAY_DARK_COLOR, fontWeight: 500 }}>{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The agent you build in the onboarding animation below.
const ONBOARD_AGENT = {
  name: "Emma Whitfield",
  email: "emma@youragency.com.au",
  phone: "0400 271 940",
  photo: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/ai_agent_demo.jpg",
};

// "Associate to <the human agent the AI works for>". Uses the param_token
// agent name when resolved, otherwise the placeholder John Doe.
function associateTitle(branding) {
  const name = branding && branding.fromToken && branding.displayBranding && branding.displayBranding.agentName
    ? branding.displayBranding.agentName
    : "John Doe";
  return `Associate to ${name}`;
}

// Agency logo + name, used to show whose team the AI agent is on. Pulls from
// the resolved branding (param_token agency, or the demo-agency fallback).
function AgencyBadge({ branding, style }) {
  const b = branding && branding.displayBranding;
  if (!b || (!b.logoUrl && !b.agencyName)) return null;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0, ...style }}>
      {b.logoUrl && (
        <img src={b.logoUrl} alt={b.agencyName || "Agency"} style={{ height: 18, width: "auto", maxWidth: 90, objectFit: "contain", display: "block" }} />
      )}
      {b.agencyName && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.agencyName}</span>
      )}
    </div>
  );
}

const ONBOARD_STEPS = [
  { label: "Details", icon: "user" },
  { label: "Photo", icon: "brand" },
  { label: "Instructions", icon: "draft" },
  { label: "Import inbox", icon: "mail" },
];

// The agent's photo, with a graceful initials fallback if the image fails to
// load.
function AgentPhoto({ size, style }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        style={{
          width: size, height: size, borderRadius: "50%",
          background: ACCENT_GRADIENT, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: size * 0.36,
          ...style,
        }}
      >
        {ONBOARD_AGENT.name.split(" ").map((w) => w[0]).join("")}
      </div>
    );
  }
  return (
    <img
      src={ONBOARD_AGENT.photo}
      alt={ONBOARD_AGENT.name}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", ...style }}
    />
  );
}

// Reveals its text one character at a time on mount (and whenever `text`
// changes), reusing the page's typewriter. Honors reduced-motion.
function TypedText({ text, speed = 20, startDelay = 150 }) {
  const n = useTypewriter(text, { startDelay, speed });
  return <>{text.slice(0, n)}</>;
}

// One read-only "input" in the onboarding form.
function OnboardField({ label, icon, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 13.5, color: "#111827", minHeight: 38, boxSizing: "border-box" }}>
        {icon && <Icon name={icon} size={14} color="#94a3b8" />}
        <span style={{ overflow: "hidden", whiteSpace: "nowrap" }}>{children}</span>
      </div>
    </div>
  );
}

// A multi-line read-only "textarea" for the instruction steps.
function OnboardArea({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>{label}</label>
      <div style={{ padding: "9px 11px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 12.5, lineHeight: 1.5, color: "#374151", minHeight: 56, whiteSpace: "pre-wrap" }}>
        {children}
      </div>
    </div>
  );
}

// Light-weight CSS confetti burst, sized to drop over the result card.
const CONFETTI_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#7c3aed"];
function ConfettiBurst() {
  return (
    <div className="vl-confetti" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 3 }}>
      {Array.from({ length: 22 }).map((_, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: -12,
            left: `${(i * 4.4 + 3) % 100}%`,
            width: 6 + (i % 3) * 2,
            height: 9 + (i % 3) * 3,
            borderRadius: 2,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            opacity: 0,
            animation: `vlConfettiFall ${1.8 + (i % 4) * 0.3}s ${(i % 6) * 0.12}s ease-in forwards`,
          }}
        />
      ))}
    </div>
  );
}

// The finished agent, revealed with a pop after "Create". A focused take on the
// hero AgentCard using the real persona photo + name.
function AgentResultCard({ title, branding, accent = BRAND_ACCENT_COLOR }) {
  const agencyName = branding && branding.displayBranding && branding.displayBranding.agencyName;
  // Readable variant of the agency colour, so light brand colours stay legible
  // as text/icons and tints on the white card.
  const solid = agentSolidColour(accent);
  return (
    <div style={{ animation: "vlPopIn 0.5s ease both", background: WHITE_COLOR, borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 20px 50px rgba(15,23,42,0.15)", padding: "1.5rem", maxWidth: 340, width: "100%", margin: "0 auto", position: "relative", zIndex: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <AgentPhoto size={60} style={{ border: "2px solid #fff", boxShadow: `0 0 18px ${agentColourAlpha(accent, 0.3)}` }} />
          <span style={{ position: "absolute", bottom: 2, right: 2, width: 13, height: 13, borderRadius: "50%", background: "#10b981", border: "2px solid #fff" }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, color: BRAND_COLOR }}>{ONBOARD_AGENT.name.split(" ")[0]}</span>
            <span style={{ background: agentColourAlpha(accent, 0.12), color: solid, fontSize: "0.6rem", fontWeight: 700, padding: "0.1rem 0.4rem", borderRadius: 4, letterSpacing: "0.05em" }}>AI AGENT</span>
          </div>
          <div style={{ fontSize: "0.83rem", color: "#64748b", fontWeight: 500 }}>{title}</div>
        </div>
      </div>
      <div style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "0.6rem", background: LIGHT_BG, border: "1px solid #e2e8f0", borderRadius: 10, padding: "0.55rem 0.75rem" }}>
        <Icon name="mail" size={15} color="#64748b" />
        <span style={{ fontSize: "0.8rem", color: GRAY_DARK_COLOR, fontWeight: 600 }}>{ONBOARD_AGENT.email}</span>
      </div>
      <div style={{ marginTop: "0.55rem", display: "flex", alignItems: "center", gap: "0.6rem", background: LIGHT_BG, border: "1px solid #e2e8f0", borderRadius: 10, padding: "0.55rem 0.75rem" }}>
        <Icon name="phone" size={15} color="#64748b" />
        <span style={{ fontSize: "0.8rem", color: GRAY_DARK_COLOR, fontWeight: 600 }}>{ONBOARD_AGENT.phone}</span>
      </div>
      {(branding && branding.displayBranding && (branding.displayBranding.logoUrl || agencyName)) && (
        <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", background: agentColourAlpha(accent, 0.08), border: `1px solid ${agentColourAlpha(accent, 0.25)}`, borderRadius: 10, padding: "0.5rem 0.75rem" }}>
          <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600 }}>Working for</span>
          <AgencyBadge branding={branding} />
        </div>
      )}
      <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.45rem", color: solid, fontSize: "0.85rem", fontWeight: 700 }}>
        <Icon name="checkCircle" size={15} color={solid} /> Agent created, now working 24/7
      </div>
    </div>
  );
}

// Self-running walkthrough of onboarding an agent: details → photo →
// instructions → import inbox → create, ending in a confetti card reveal.
function OnboardingFlow({ isMobile, branding }) {
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // 0–3 = the four steps, 4 = created (confetti + card).
  const [step, setStep] = useState(0);
  const [contacts, setContacts] = useState(0);
  // Tint the flow with the agency's colour to show whose team the agent is on.
  const accent = (branding && branding.displayBranding && branding.displayBranding.colour) || BRAND_ACCENT_COLOR;
  // Readable variant for solid fills (with white text/icons) and for accent text
  // on white, so a light agency colour stays legible throughout the animation.
  const solid = agentSolidColour(accent);
  const agentTitle = associateTitle(branding);

  // Drive the walkthrough forward, then loop.
  useEffect(() => {
    if (prefersReduced) {
      if (step !== 4) { setStep(4); return undefined; }
      const t = setTimeout(() => setStep(0), 8000);
      return () => clearTimeout(t);
    }
    const DURATIONS = [5200, 3400, 5800, 5000, 7000];
    const t = setTimeout(() => setStep((s) => (s + 1) % 5), DURATIONS[step]);
    return () => clearTimeout(t);
  }, [step, prefersReduced]);

  // Count contacts up while the inbox import step is on screen.
  useEffect(() => {
    if (step !== 3) { setContacts(0); return undefined; }
    if (prefersReduced) { setContacts(1248); return undefined; }
    let raf;
    const start = performance.now();
    const target = 1248;
    const dur = 3800;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setContacts(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step, prefersReduced]);

  const created = step === 4;

  return (
    <div
      style={{
        position: "relative",
        background: WHITE_COLOR,
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
        padding: isMobile ? "1.4rem" : "1.75rem",
        overflow: "hidden",
        minHeight: 460,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Window chrome + title, with the agency it's being created for. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.1rem" }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#f87171" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fbbf24" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#34d399" }} />
        <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 700, color: BRAND_COLOR }}>Create your agent</span>
        <AgencyBadge branding={branding} style={{ marginLeft: "auto" }} />
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: "1.25rem" }}>
        {ONBOARD_STEPS.map((s, i) => {
          const done = created || i < step;
          const active = !created && i === step;
          return (
            <React.Fragment key={s.label}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: "0 0 auto" }}>
                <div
                  style={{
                    width: 30, height: 30, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: done ? solid : active ? agentColourAlpha(accent, 0.12) : "#f1f5f9",
                    color: done ? "#fff" : active ? solid : "#94a3b8",
                    border: active ? `1px solid ${solid}` : "1px solid transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  {done ? <Icon name="checkCircle" size={15} color="#fff" /> : <Icon name={s.icon} size={14} />}
                </div>
                {!isMobile && (
                  <span style={{ fontSize: 9.5, fontWeight: 600, color: active ? BRAND_COLOR : "#94a3b8", whiteSpace: "nowrap" }}>{s.label}</span>
                )}
              </div>
              {i < ONBOARD_STEPS.length - 1 && (
                <div style={{ flex: 1, height: 2, background: (created || i < step) ? solid : "#e5e7eb", borderRadius: 2, transition: "background 0.3s ease", marginBottom: isMobile ? 0 : 16 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step body */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: created ? "center" : "flex-start" }}>
        {created ? (
          <>
            <ConfettiBurst />
            <AgentResultCard title={agentTitle} branding={branding} accent={accent} />
          </>
        ) : (
          <div key={step} style={{ animation: prefersReduced ? "none" : "vlStepFade 0.4s ease" }}>
            {step === 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR, marginBottom: 12 }}>1. Add the details</div>
                <OnboardField label="Agent name" icon="user"><TypedText text={ONBOARD_AGENT.name} speed={45} startDelay={300} /></OnboardField>
                <OnboardField label="Title"><TypedText text={agentTitle} speed={45} startDelay={1500} /></OnboardField>
                <OnboardField label="Email on your domain" icon="mail"><TypedText text={ONBOARD_AGENT.email} speed={45} startDelay={2800} /></OnboardField>
                <OnboardField label="Phone number" icon="phone"><TypedText text={ONBOARD_AGENT.phone} speed={45} startDelay={4000} /></OnboardField>
              </>
            )}

            {step === 1 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR, marginBottom: 16 }}>2. Add the photo</div>
                <div style={{ position: "relative", width: 150, height: 150, margin: "0 auto", borderRadius: "50%", border: "2px dashed #cbd5e1", padding: 6 }}>
                  <AgentPhoto size={134} style={{ animation: prefersReduced ? "none" : "vlPopIn 0.5s ease both" }} />
                  <span style={{ position: "absolute", bottom: 6, right: 6, width: 30, height: 30, borderRadius: "50%", background: solid, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
                    <Icon name="checkCircle" size={16} color="#fff" />
                  </span>
                </div>
                <div style={{ marginTop: 14, fontSize: 13, color: "#64748b" }}>Profile photo uploaded</div>
              </div>
            )}

            {step === 2 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR, marginBottom: 12 }}>3. Add the instructions</div>
                <OnboardArea label="How to reply">
                  <TypedText speed={22} startDelay={300} text="Reply within the hour. Warm and concise, never pushy. Offer a call the moment a seller shows real intent." />
                </OnboardArea>
                <OnboardArea label="How to handle appraisals">
                  <TypedText speed={22} startDelay={2800} text="Use recent comparable sales within 1km. Always give a price range, then invite them to book a market appraisal." />
                </OnboardArea>
              </>
            )}

            {step === 3 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_COLOR, marginBottom: 12 }}>4. Import your mailbox</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                  {[{ n: "Gmail", on: true }, { n: "Outlook", on: false }, { n: "Any IMAP", on: false }].map((p) => (
                    <span key={p.n} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 999, border: `1px solid ${p.on ? solid : "#e5e7eb"}`, background: p.on ? agentColourAlpha(accent, 0.08) : "#fff", color: p.on ? solid : "#64748b" }}>
                      <Icon name="mail" size={11} color={p.on ? solid : "#94a3b8"} />
                      {p.n}{p.on ? " · connected" : ""}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 13, color: GRAY_DARK_COLOR, marginBottom: 8 }}>
                  Reading <strong style={{ color: BRAND_COLOR }}>{contacts.toLocaleString()}</strong> conversations…
                </div>
                <div style={{ height: 8, borderRadius: 999, background: "#eef2f6", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.round((contacts / 1248) * 100)}%`, background: solid, borderRadius: 999, transition: "width 0.1s linear" }} />
                </div>
                <div style={{ marginTop: 10, fontSize: 11.5, color: "#94a3b8" }}>
                  Building a relationship summary for every contact, so your agent walks in fully briefed.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Agency branding: a marketing-site visitor who arrives from a campaign link
// carries a param_token that resolves (via /activate-trial-info) to their
// agency's branding. We use it to swap generic demo links for agency-branded
// CTAs and to power the per-card "send me a sample" flows.
// =============================================================================
function useAgencyBranding() {
  const [state, setState] = useState({
    token: null,
    slug: "",
    branding: null,        // branding from a real param_token (gates branded CTAs)
    ready: false,          // token present AND agency data came back successfully
    resolved: false,
    displayBranding: null, // branding to show off (token agency, else demo-agency)
    fromToken: false,      // true when displayBranding came from a real param_token
  });

  useEffect(() => {
    let cancelled = false;
    const getToken = () => {
      try {
        return localStorage.getItem("param_token");
      } catch (_) {
        return null;
      }
    };

    const mapBranding = (data) => ({
      logoUrl: data.logo_url || "",
      colour: data.agent_colour || BRAND_ACCENT_COLOR,
      agencyName: data.agency_name || "",
      agentName: data.agent_name || "",
      agentFirstName: data.agent_first_name || "",
      agentEmail: data.agent_email || data.email || "",
      profileImage: data.agent_profile_image || "",
      agencyId: data.agency_id || "",
      agentId: data.agent_id || "",
      slug: data.landing_page_slug || "",
    });

    const resolve = (body) =>
      fetch(`${window.api_host}/activate-trial-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then((r) => r.json());

    // Fall back to the demo-agency branding so the animation always has a logo
    // and agency name to show, even for an anonymous visitor.
    const loadDemoFallback = () => {
      resolve({ slug: "demo-agency" })
        .then((data) => {
          if (cancelled) return;
          if (data && data.status === "success") {
            setState((s) => ({ ...s, resolved: true, displayBranding: mapBranding(data), fromToken: false }));
          } else {
            setState((s) => ({ ...s, resolved: true }));
          }
        })
        .catch(() => {
          if (!cancelled) setState((s) => ({ ...s, resolved: true }));
        });
    };

    // Set once we've shown branding resolved from a real param_token, so a late
    // token arrival doesn't redundantly re-resolve.
    let gotTokenBranding = false;

    const resolveToken = (token) =>
      resolve({ token })
        .then((data) => {
          if (cancelled) return;
          if (!data || data.status !== "success" || !data.landing_page_slug) {
            setState((s) => ({ ...s, token }));
            loadDemoFallback();
            return;
          }
          gotTokenBranding = true;
          const b = mapBranding(data);
          setState({
            token,
            slug: data.landing_page_slug,
            branding: b,
            ready: true,
            resolved: true,
            displayBranding: b,
            fromToken: true,
          });
        })
        .catch(() => {
          if (!cancelled) loadDemoFallback();
        });

    const initial = getToken();
    if (initial) {
      resolveToken(initial);
    } else {
      loadDemoFallback();
    }

    // A short ?t= code is resolved to a param_token asynchronously (in App's
    // TokenIngest) and can land after this first read fell back to the demo
    // agency. Re-resolve when it arrives so the real agency branding replaces
    // the demo one without a refresh.
    const onTokenReady = () => {
      if (cancelled || gotTokenBranding) return;
      const t = getToken();
      if (t) resolveToken(t);
    };
    window.addEventListener("param-token-ready", onTokenReady);

    return () => {
      cancelled = true;
      window.removeEventListener("param-token-ready", onTokenReady);
    };
  }, []);

  return state;
}

// Turn the generic demo URL (agent=demo-agency) into the agency-branded one.
function brandedDemoUrl(demoUrl, slug, token) {
  if (!demoUrl) return demoUrl;
  let url = demoUrl.replace("agent=demo-agency", `agent=${encodeURIComponent(slug)}`);
  if (token) url += (url.includes("?") ? "&" : "?") + `param_token=${encodeURIComponent(token)}`;
  return url;
}

// Convert a hex colour to an rgba() string (used for the pulse halo).
const SAMPLE_PROPERTY_ID = "vl3272ep";
const SAMPLE_PROPERTY_SLUG = "22-sevira-avenue-gledswood-hills-nsw-2557";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MOBILE_RE = /^(\+?61|0)4\d{2}\s?\d{3}\s?\d{3}$/;

// Pulsating, agency-branded link button: logo (if any) + label. Used for the
// Valuator demo and the branded sample article.
function BrandedLinkButton({ feature, branding, slug, token }) {
  const accent = branding.colour || BRAND_ACCENT_COLOR;
  // Darkened if the brand colour is light, so the white label stays legible.
  const solid = agentSolidColour(accent);
  const href = brandedDemoUrl(feature.demoUrl, slug, token);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-track-event="true"
      className="vl-pulse"
      style={{
        "--pulse-rgba": agentColourAlpha(accent, 0.5),
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        background: solid,
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.85rem",
        padding: "0.55rem 1rem",
        borderRadius: "8px",
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      {branding.logoUrl && (
        <img
          src={branding.logoUrl}
          alt={branding.agencyName || "Agency"}
          style={{ height: 18, width: "auto", maxWidth: 70, objectFit: "contain" }}
        />
      )}
      {feature.branded.label || "Demo"} ↗
    </a>
  );
}

// Agency-branded "send me a sample" control: an input + a Send button that hits
// the matching settings sample endpoint, authenticated by the param_token.
function BrandedSendControl({ feature, branding, slug }) {
  const accent = branding.colour || BRAND_ACCENT_COLOR;
  // Darkened if the brand colour is light, so the white Send label stays legible.
  const solid = agentSolidColour(accent);
  const cfg = feature.branded;
  const isSms = cfg.channel === "sms";
  const [value, setValue] = useState(!isSms && branding.agentEmail ? branding.agentEmail : "");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);

  const send = async () => {
    const to = value.trim();
    if (isSms) {
      if (!MOBILE_RE.test(to.replace(/\s/g, ""))) {
        setOk(false);
        setMsg("Enter a valid Australian mobile.");
        return;
      }
    } else if (!EMAIL_RE.test(to)) {
      setOk(false);
      setMsg("Enter a valid email address.");
      return;
    }
    setSending(true);
    setMsg("");
    try {
      let payload;
      if (cfg.endpoint === "send-property-report") {
        payload = {
          email: to,
          property_id: SAMPLE_PROPERTY_ID,
          property_slug: SAMPLE_PROPERTY_SLUG,
          agency_id: branding.agencyId,
          agent_id: branding.agentId,
          agent_slug: slug,
          report_type: cfg.reportType || "property_update",
          source: "landing_sample_send",
        };
      } else if (cfg.endpoint === "send-market-update-sample") {
        payload = { sample_email: to };
      } else {
        payload = { mobile: to }; // SMS endpoints
      }
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/${cfg.endpoint}`,
        payload,
        { "Content-Type": "application/json" }
      );
      const success =
        res?.data?.status === "success" || (res?.status === 200 && !res?.data?.error);
      setOk(success);
      setMsg(
        success
          ? `Sample sent to ${to}.`
          : res?.data?.message || res?.data?.error || "Failed to send sample."
      );
      if (success) setValue("");
    } catch {
      setOk(false);
      setMsg("Failed to send sample. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ marginTop: "0.75rem" }}>
      {/* One integrated pill: agency logo · input · attached send button. */}
      <div
        className="vl-pulse"
        style={{
          "--pulse-rgba": agentColourAlpha(accent, 0.45),
          display: "flex",
          alignItems: "stretch",
          border: `1.5px solid ${solid}`,
          borderRadius: "10px",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <input
          type={isSms ? "tel" : "email"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !sending) send();
          }}
          placeholder={cfg.placeholder}
          disabled={sending}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.85rem",
            padding: "0.55rem 0.7rem",
            border: "none",
            outline: "none",
            background: "transparent",
          }}
        />
        <button
          onClick={send}
          disabled={sending}
          data-track-event="true"
          style={{
            border: "none",
            background: solid,
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.85rem",
            padding: "0 1rem",
            cursor: sending ? "default" : "pointer",
            opacity: sending ? 0.7 : 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            whiteSpace: "nowrap",
          }}
        >
          <Icon name="send" size={14} color="#fff" />
          {sending ? "Sending…" : cfg.cta || "Send"}
        </button>
      </div>
      {msg && (
        <div
          style={{
            marginTop: "0.4rem",
            fontSize: "0.78rem",
            color: ok ? BRAND_ACCENT_COLOR : "#dc2626",
          }}
        >
          {msg}
        </div>
      )}
    </div>
  );
}

// A single feature, rendered as compact proof inside a job: icon, not screenshot.
function FeatureCard({ feature, accent, isMobile, branding }) {
  // Branded controls only appear when the visitor's agency resolved successfully.
  const showBranded = branding && branding.ready && !!feature.branded;
  const isBrandedLink = showBranded && feature.branded.kind === "link";
  const isBrandedSend = showBranded && feature.branded.kind === "send";

  return (
    <div
      style={{
        flex: "1 1 300px",
        maxWidth: "360px",
        background: WHITE_COLOR,
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        padding: isMobile ? "1.5rem" : "1.75rem",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 14px 34px rgba(0,0,0,0.12)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.05)";
      }}
    >
      <IconTile name={feature.icon} color={accent} style={{ marginBottom: "1.1rem" }} />
      <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1.05rem", fontWeight: 700, color: BRAND_COLOR }}>
        {feature.title}
      </h3>
      <p style={{ margin: 0, fontSize: "0.88rem", color: GRAY_DARK_COLOR, lineHeight: 1.55, flex: 1 }}>
        {feature.description}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: "1.25rem" }}>
        <Link to={feature.to} style={{ color: BRAND_ACCENT_COLOR, fontWeight: 600, fontSize: "0.85rem", textDecoration: "none" }}>
          Learn more →
        </Link>
        {isBrandedLink ? (
          <BrandedLinkButton feature={feature} branding={branding.branding} slug={branding.slug} token={branding.token} />
        ) : (
          /* Hide the generic "See demo" once any branded control is present. */
          !showBranded && feature.demoUrl && (
            <a
              href={feature.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#64748b", fontWeight: 600, fontSize: "0.85rem", textDecoration: "none" }}
            >
              See demo ↗
            </a>
          )
        )}
      </div>
      {isBrandedSend && (
        <BrandedSendControl feature={feature} branding={branding.branding} slug={branding.slug} />
      )}
    </div>
  );
}

// =============================================================================
// LIVE EVENT STREAM: a self-running, "basic /user-activity" feed that shows
// seller-intent events landing in the queue in real time. Pure decoration -
// the data is synthetic but realistic, to make Job 3 feel alive.
// =============================================================================
const STREAM_FIRST_NAMES = [
  "Sarah", "James", "Priya", "Michael", "Emma", "Daniel", "Olivia", "Liam",
  "Sophie", "Noah", "Chloe", "Ethan", "Grace", "Lucas", "Ava", "Mason",
  "Isla", "Ryan", "Mia", "Jack", "Zoe", "Harper", "Leo", "Amelia",
];
const STREAM_LAST_INITIALS = ["B.", "T.", "K.", "M.", "S.", "L.", "R.", "N.", "P.", "W.", "C.", "H."];
const STREAM_STREETS = [
  "12 Sevira Ave", "8 Rosewood Cres", "44 Marsden Rd", "21 Hilltop St",
  "5 Banksia Pl", "17 Carrington Dr", "33 Foxall Rd", "9 Lakeside Bvd",
  "26 Camden Valley Way", "61 Riverstone Pde", "14 Gledswood Cct", "3 Oran Park Dr",
];
const STREAM_SUBURBS = [
  "Gledswood Hills", "Oran Park", "Leppington", "Camden", "Liverpool",
  "Gregory Hills", "Harrington Park", "Narellan", "Edmondson Park", "Catherine Field",
];
const STREAM_CAMPAIGNS = [
  "March Market Update", "Spring Sellers Guide", "Suburb Price Report",
  "Just Listed Nearby", "Auction Results Wrap", "Rate Cut Insights",
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const streamContact = () => `${pick(STREAM_FIRST_NAMES)} ${pick(STREAM_LAST_INITIALS)}`;

// Weighted templates: each returns the row content. Colours mirror the
// user-activity palette so it reads as the real timeline, distilled.
const STREAM_TEMPLATES = [
  { icon: "chart", label: "Viewed property analysis", color: "#7c3aed", weight: 5, detail: () => pick(STREAM_STREETS) },
  { icon: "report", label: "Requested property report", color: "#2563eb", weight: 3, detail: () => pick(STREAM_STREETS) },
  { icon: "report", label: "Property report sent", color: "#6d28d9", weight: 3, detail: () => pick(STREAM_STREETS) },
  { icon: "mapPin", label: "Viewed suburb report", color: "#b45309", weight: 3, detail: () => pick(STREAM_SUBURBS) },
  { icon: "appraisal", label: "Viewed appraisal", color: "#be123c", weight: 2, detail: () => pick(STREAM_STREETS) },
  { icon: "mail", label: "Email opened", color: "#047857", weight: 5, detail: () => pick(STREAM_CAMPAIGNS), email: true },
  { icon: "mail", label: "Email link clicked", color: "#4338ca", weight: 4, detail: () => pick(STREAM_CAMPAIGNS), email: true },
  { icon: "send", label: "Email delivered", color: "#1d4ed8", weight: 3, detail: () => pick(STREAM_CAMPAIGNS), email: true },
  { icon: "message", label: "SMS reply received", color: "#0369a1", weight: 2, detail: () => "“Still keen, call me?”" },
  { icon: "message", label: "SMS delivered", color: "#0891b2", weight: 2, detail: () => pick(STREAM_CAMPAIGNS) },
  { icon: "qr", label: "Scanned QR flyer", color: "#c2410c", weight: 2, detail: () => pick(STREAM_SUBURBS) },
  { icon: "draft", label: "Started enquiry", color: "#be185d", weight: 1, detail: () => pick(STREAM_STREETS) },
  { icon: "home", label: "Valued their property", color: "#15803d", weight: 4, detail: () => pick(STREAM_STREETS) },
  { icon: "user", label: "Signed in", color: "#475569", weight: 1, detail: () => null },
];

// Pre-expand the weighted list once.
const STREAM_WEIGHTED = STREAM_TEMPLATES.flatMap((t) => Array(t.weight).fill(t));

let streamSeq = 0;
function makeStreamEvent() {
  const t = pick(STREAM_WEIGHTED);
  return {
    id: ++streamSeq,
    icon: t.icon,
    label: t.label,
    color: t.color,
    email: !!t.email,
    contact: streamContact(),
    detail: t.detail(),
    landedAt: Date.now(),
    // Random drop point on the fake map (kept off the very edges).
    x: 8 + Math.random() * 84,
    y: 14 + Math.random() * 68,
  };
}

function streamAgo(ms) {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 4) return "Just now";
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
}

function StreamRow({ ev, isMobile }) {
  return (
    <div
      className="vl-stream-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "0.7rem" : "0.9rem",
        padding: isMobile ? "0.7rem 0.9rem" : "0.8rem 1rem",
        borderBottom: "1px solid #f3f4f6",
        background: "#fff",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `${ev.color}1A`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={ev.icon} size={16} color={ev.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span
            style={{
              borderRadius: 16,
              padding: "2px 9px",
              fontWeight: 600,
              fontSize: 11,
              background: `${ev.color}1A`,
              color: ev.color,
              border: `1px solid ${ev.color}40`,
              whiteSpace: "nowrap",
            }}
          >
            {ev.label}
          </span>
          {ev.email && (
            <span style={{ borderRadius: 16, padding: "2px 7px", fontSize: 10, background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
              Email
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: 3, minWidth: 0 }}>
          <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, whiteSpace: "nowrap" }}>{ev.contact}</span>
          {ev.detail && (
            <span style={{ fontSize: 12.5, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              · {ev.detail}
            </span>
          )}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", flexShrink: 0 }}>
        {streamAgo(ev.landedAt)}
      </div>
    </div>
  );
}

// Non-interactive, stylised map. Each live event drops a colour-coded, pulsing
// pin where it "happened", and the newest gets a floating callout - the map
// twin of the list feed.
function StreamMap({ events, isMobile }) {
  const pins = events.slice(0, 6);
  const newest = pins[0];
  return (
    <div
      style={{
        position: "relative",
        height: isMobile ? 320 : 380,
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        background: "#e8eef3",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {/* Faux map: water, parks, and a street grid. */}
      <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 380" style={{ position: "absolute", inset: 0, display: "block" }}>
        <rect width="600" height="380" fill="#e9eef3" />
        {/* Water */}
        <path d="M0 300 Q 120 270 240 300 T 600 290 L600 380 L0 380 Z" fill="#cfe3ef" />
        {/* Parks */}
        <rect x="40" y="40" width="120" height="80" rx="10" fill="#d6ead7" />
        <rect x="420" y="60" width="130" height="90" rx="10" fill="#d6ead7" />
        <rect x="250" y="170" width="90" height="70" rx="10" fill="#d6ead7" />
        {/* Major roads */}
        <g stroke="#ffffff" strokeWidth="9" strokeLinecap="round">
          <line x1="0" y1="150" x2="600" y2="130" />
          <line x1="0" y1="250" x2="600" y2="240" />
          <line x1="180" y1="0" x2="200" y2="380" />
          <line x1="400" y1="0" x2="420" y2="380" />
        </g>
        {/* Minor streets */}
        <g stroke="#ffffff" strokeWidth="4" strokeLinecap="round" opacity="0.9">
          <line x1="0" y1="70" x2="600" y2="60" />
          <line x1="0" y1="200" x2="600" y2="190" />
          <line x1="0" y1="320" x2="600" y2="310" />
          <line x1="90" y1="0" x2="100" y2="380" />
          <line x1="290" y1="0" x2="305" y2="380" />
          <line x1="510" y1="0" x2="520" y2="380" />
        </g>
      </svg>

      {/* Live pins */}
      {pins.map((ev, i) => (
        <div
          key={ev.id}
          className={i === 0 ? "vl-map-pin vl-map-pin-new" : "vl-map-pin"}
          style={{
            position: "absolute",
            left: `${ev.x}%`,
            top: `${ev.y}%`,
            transform: "translate(-50%, -100%)",
            opacity: i === 0 ? 1 : Math.max(0.35, 1 - i * 0.14),
            zIndex: pins.length - i,
          }}
        >
          {/* Pulsing halo */}
          {i === 0 && (
            <span
              className="vl-pulse"
              style={{
                "--pulse-rgba": `${ev.color}80`,
                position: "absolute",
                left: "50%",
                top: "100%",
                transform: "translate(-50%, -50%)",
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: ev.color,
              }}
            />
          )}
          {/* Teardrop pin with icon */}
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50% 50% 50% 0",
              transform: "rotate(-45deg)",
              background: ev.color,
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #fff",
            }}
          >
            <span style={{ transform: "rotate(45deg)", display: "flex" }}>
              <Icon name={ev.icon} size={13} color="#fff" />
            </span>
          </div>
        </div>
      ))}

      {/* Callout for the newest event */}
      {newest && (
        <div
          key={`callout-${newest.id}`}
          className="vl-map-callout"
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            right: 12,
            maxWidth: 300,
            background: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(4px)",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            padding: "0.6rem 0.75rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
          }}
        >
          <div style={{ width: 32, height: 32, borderRadius: 9, background: `${newest.color}1A`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name={newest.icon} size={15} color={newest.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: newest.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{newest.label}</div>
            <div style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {newest.contact}{newest.detail ? ` · ${newest.detail}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HeroSignalMap - a standalone, toggle-free version of the live map used in the
// main hero. Unlike StreamMap, every drop is framed as the AI agent *notifying*
// you of a user's action, and the callout carries that contact's lead score and
// AI-applied tags - so the hero shows the agent surfacing ready-to-sell leads.
// (Intentionally duplicated from StreamMap so the hero treatment can diverge.)
// =============================================================================

// Pool of AI-applied tags a signal might carry, themed by intent.
const HERO_SIGNAL_TAGS = [
  { label: "Hot lead", color: "#ef4444" },
  { label: "Seller", color: "#0d9488" },
  { label: "Downsizer", color: "#f97316" },
  { label: "Upsizer", color: "#22c55e" },
  { label: "Investor", color: "#6366f1" },
  { label: "Empty nester", color: "#d946ef" },
  { label: "Nurturing", color: "#f59e0b" },
  { label: "First home buyer", color: "#06b6d4" },
];

// High-intent actions earn a hotter score and a "Hot lead" tag; everything else
// lands warm/cool. Mirrors the spirit of the in-app lead score.
const HERO_HOT_LABELS = new Set([
  "Viewed property analysis", "Viewed appraisal", "SMS reply received",
  "Requested property report", "Valued their property",
]);

let heroSeq = 0;
function makeHeroSignal() {
  const t = pick(STREAM_WEIGHTED);
  const hot = HERO_HOT_LABELS.has(t.label);
  const score = hot
    ? 74 + Math.floor(Math.random() * 24) // 74-97
    : 32 + Math.floor(Math.random() * 36); // 32-67
  // 1-2 AI tags: hot signals always lead with "Hot lead".
  const pool = HERO_SIGNAL_TAGS.filter((tag) => tag.label !== "Hot lead" && tag.label !== "Nurturing");
  const intent = pick(pool);
  const tags = hot
    ? [HERO_SIGNAL_TAGS[0], intent]
    : [{ label: "Nurturing", color: "#f59e0b" }, intent];
  return {
    id: ++heroSeq,
    icon: t.icon,
    label: t.label,
    color: t.color,
    contact: streamContact(),
    detail: t.detail(),
    score,
    tags,
    landedAt: Date.now(),
    x: 8 + Math.random() * 84,
    // Keep pins in the upper area so they never drop onto the callout card,
    // which is pinned to the bottom of the map.
    y: 10 + Math.random() * 42,
  };
}

function HeroSignalMap({ isMobile }) {
  const [events, setEvents] = useState(() =>
    Array.from({ length: 5 }, () => {
      const e = makeHeroSignal();
      e.landedAt = Date.now() - Math.floor(Math.random() * 90000);
      return e;
    }).sort((a, b) => b.landedAt - a.landedAt)
  );

  // Land a new signal on a varying cadence.
  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return undefined;
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setEvents((prev) => [makeHeroSignal(), ...prev].slice(0, 6));
        schedule();
      }, 650 + Math.random() * 700);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  // Tick so relative timestamps stay live.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const pins = events.slice(0, 6);
  const newest = pins[0];
  const tier = newest ? streamScoreTier(newest.score) : null;

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto" }}>
      <style>{`
        @keyframes vlPinDrop {
          0% { opacity: 0; transform: translate(-50%, -160%) scale(0.6); }
          60% { opacity: 1; transform: translate(-50%, -92%) scale(1.08); }
          100% { transform: translate(-50%, -100%) scale(1); }
        }
        .vl-hero-pin-new { animation: vlPinDrop 0.5s cubic-bezier(0.22, 1, 0.36, 1); }
        @keyframes vlHeroCalloutIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .vl-hero-callout { animation: vlHeroCalloutIn 0.45s ease; }
        @media (prefers-reduced-motion: reduce) {
          .vl-hero-pin-new, .vl-hero-callout { animation: none; }
        }
      `}</style>

      {/* LIVE pill */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.85rem" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: "rgba(16,185,129,0.15)", color: "#34d399", fontSize: "0.78rem", fontWeight: 700, borderRadius: "999px", padding: "0.3rem 0.85rem", letterSpacing: "0.03em" }}>
          <span className="vl-pulse" style={{ "--pulse-rgba": "rgba(16,185,129,0.6)", width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} />
          LIVE · Lead signals landing now
        </span>
      </div>

      <div
        style={{
          position: "relative",
          height: isMobile ? 360 : 420,
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          background: "#e8eef3",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        {/* Faux map: water, parks, and a street grid. */}
        <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 420" style={{ position: "absolute", inset: 0, display: "block" }}>
          <rect width="600" height="420" fill="#e9eef3" />
          <path d="M0 330 Q 120 300 240 330 T 600 320 L600 420 L0 420 Z" fill="#cfe3ef" />
          <rect x="40" y="40" width="120" height="80" rx="10" fill="#d6ead7" />
          <rect x="420" y="60" width="130" height="90" rx="10" fill="#d6ead7" />
          <rect x="250" y="180" width="90" height="70" rx="10" fill="#d6ead7" />
          <g stroke="#ffffff" strokeWidth="9" strokeLinecap="round">
            <line x1="0" y1="150" x2="600" y2="130" />
            <line x1="0" y1="270" x2="600" y2="260" />
            <line x1="180" y1="0" x2="200" y2="420" />
            <line x1="400" y1="0" x2="420" y2="420" />
          </g>
          <g stroke="#ffffff" strokeWidth="4" strokeLinecap="round" opacity="0.9">
            <line x1="0" y1="70" x2="600" y2="60" />
            <line x1="0" y1="210" x2="600" y2="200" />
            <line x1="0" y1="350" x2="600" y2="340" />
            <line x1="90" y1="0" x2="100" y2="420" />
            <line x1="290" y1="0" x2="305" y2="420" />
            <line x1="510" y1="0" x2="520" y2="420" />
          </g>
        </svg>

        {/* Live pins */}
        {pins.map((ev, i) => (
          <div
            key={ev.id}
            className={i === 0 ? "vl-map-pin vl-hero-pin-new" : "vl-map-pin"}
            style={{
              position: "absolute",
              left: `${ev.x}%`,
              top: `${ev.y}%`,
              transform: "translate(-50%, -100%)",
              opacity: i === 0 ? 1 : Math.max(0.35, 1 - i * 0.14),
              zIndex: pins.length - i,
            }}
          >
            {i === 0 && (
              <span
                className="vl-pulse"
                style={{
                  "--pulse-rgba": `${ev.color}80`,
                  position: "absolute", left: "50%", top: "100%",
                  transform: "translate(-50%, -50%)",
                  width: 14, height: 14, borderRadius: "50%", background: ev.color,
                }}
              />
            )}
            <div
              style={{
                width: 30, height: 30,
                borderRadius: "50% 50% 50% 0",
                transform: "rotate(-45deg)",
                background: ev.color,
                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #fff",
              }}
            >
              <span style={{ transform: "rotate(45deg)", display: "flex" }}>
                <Icon name={ev.icon} size={13} color="#fff" />
              </span>
            </div>
          </div>
        ))}

        {/* AI-agent notification callout for the newest signal */}
        {newest && (
          <div
            key={`hero-callout-${newest.id}`}
            className="vl-hero-callout"
            style={{
              position: "absolute",
              left: 12, right: 12, bottom: 12,
              zIndex: 20,
              maxWidth: 340,
              background: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(4px)",
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
              padding: "0.7rem 0.8rem",
            }}
          >
            {/* "Your AI agent is notifying you" header */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <span style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={ONBOARD_AGENT.photo}
                  alt=""
                  style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #fff", boxShadow: "0 0 0 1px #e2e8f0" }}
                />
                <span style={{ position: "absolute", right: -2, bottom: -2, width: 12, height: 12, borderRadius: "50%", background: BRAND_ACCENT_COLOR, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="sparkles" size={7} color="#fff" />
                </span>
              </span>
              <span style={{ fontSize: 11.5, color: "#475569", fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <strong style={{ color: BRAND_COLOR }}>{ONBOARD_AGENT.name.split(" ")[0]}</strong> spotted a lead signal
              </span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>{streamAgo(newest.landedAt)}</span>
            </div>

            {/* The action */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: `${newest.color}1A`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={newest.icon} size={14} color={newest.color} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: newest.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{newest.label}</div>
                <div style={{ fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {newest.contact}{newest.detail ? ` · ${newest.detail}` : ""}
                </div>
              </div>
              {/* Lead score */}
              {tier && (
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "4px 9px", borderRadius: 999, flexShrink: 0,
                    background: tier.bg, border: `1px solid ${tier.border}`, color: tier.fg,
                    fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                  }}
                >
                  {newest.score >= 70 && <Icon name="trendingUp" size={11} color={tier.fg} />}
                  <span style={{ opacity: 0.8, fontWeight: 600 }}>Score</span>
                  <span style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{newest.score}</span>
                </span>
              )}
            </div>

            {/* AI-applied tags */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: "0.55rem", flexWrap: "wrap" }}>
              {newest.tags.map((tag) => (
                <span
                  key={tag.label}
                  title={`AI applied "${tag.label}"`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 3,
                    padding: "2px 8px", borderRadius: 999,
                    background: `${tag.color}1A`, color: tag.color,
                    border: `1px solid ${tag.color}40`,
                    fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
                  }}
                >
                  <Icon name="sparkles" size={9} color={tag.color} />
                  {tag.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// "Contacts" tab - a marketing twin of the in-app /contacts list. Each row
// leads with an AI-applied tag set and a prominent, colour-tiered lead score.
// Hovering / tapping the score reveals the same breakdown popover agents see
// in the product, so the demo lands the promise: "your agent tells you exactly
// who's ready to sell."
// =============================================================================

// Lead-score tiers - mirrors LeadScoreBadge.tierFor so the colours read the
// same here as they do inside the app.
function streamScoreTier(n) {
  if (n >= 90) return { label: "Blazing", bg: "#fee2e2", border: "#fca5a5", fg: "#7f1d1d", dot: "#dc2626" };
  if (n >= 70) return { label: "Hot",     bg: "#fee2e2", border: "#fecaca", fg: "#991b1b", dot: "#ef4444" };
  if (n >= 55) return { label: "Warm",    bg: "#ffedd5", border: "#fed7aa", fg: "#9a3412", dot: "#f97316" };
  if (n >= 40) return { label: "Mild",    bg: "#fef3c7", border: "#fde68a", fg: "#92400e", dot: "#f59e0b" };
  if (n >= 20) return { label: "Cool",    bg: "#dbeafe", border: "#bfdbfe", fg: "#1e3a8a", dot: "#3b82f6" };
  return            { label: "Cold",    bg: "#f1f5f9", border: "#e2e8f0", fg: "#334155", dot: "#64748b" };
}

// Stylised seller-intent book. Tags are AI-applied (source: "ai") so every row
// shows the robot marker; `signals` feeds the score breakdown popover.
const STREAM_CONTACTS = [
  {
    id: "c1", name: "Sarah Mitchell", suburb: "Balmain NSW", ago: "2m ago",
    score: 94,
    tags: [
      { label: "Hot lead", color: "#ef4444" },
      { label: "Seller", color: "#0d9488" },
      { label: "Downsizer", color: "#f97316" },
    ],
    signals: [
      { label: "Property analysis", count: 3, weight: 25 },
      { label: "SMS reply", count: 1, weight: 20 },
      { label: "Email click", count: 4, weight: 10 },
    ],
    rankPct: 2,
  },
  {
    id: "c2", name: "James Nguyen", suburb: "Hawthorn VIC", ago: "1h ago",
    score: 81,
    tags: [
      { label: "Hot lead", color: "#ef4444" },
      { label: "Upsizer", color: "#22c55e" },
    ],
    signals: [
      { label: "Property analysis", count: 2, weight: 25 },
      { label: "Email click", count: 3, weight: 10 },
      { label: "Property view", count: 5, weight: 5 },
    ],
    rankPct: 6,
  },
  {
    id: "c3", name: "Priya Sharma", suburb: "Toowong QLD", ago: "4h ago",
    score: 63,
    tags: [
      { label: "Warm", color: "#f59e0b" },
      { label: "Seller", color: "#0d9488" },
    ],
    signals: [
      { label: "Property analysis", count: 1, weight: 25 },
      { label: "Email open", count: 6, weight: 2 },
      { label: "Suburb search", count: 3, weight: 5 },
    ],
    rankPct: 18,
  },
  {
    id: "c4", name: "Olivia Brown", suburb: "Subiaco WA", ago: "1d ago",
    score: 47,
    tags: [
      { label: "Nurturing", color: "#f59e0b" },
      { label: "Empty nester", color: "#d946ef" },
    ],
    signals: [
      { label: "Email click", count: 2, weight: 10 },
      { label: "Property view", count: 3, weight: 5 },
      { label: "Email open", count: 4, weight: 2 },
    ],
    rankPct: 34,
  },
  {
    id: "c5", name: "Daniel Lee", suburb: "Newtown NSW", ago: "3d ago",
    score: 28,
    tags: [
      { label: "Cool", color: "#3b82f6" },
      { label: "Buyer", color: "#3b82f6" },
    ],
    signals: [
      { label: "Email open", count: 3, weight: 2 },
      { label: "Suburb search", count: 1, weight: 5 },
    ],
    rankPct: 61,
  },
];

const streamInitials = (name) =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

// The AI-tagged lead score with a hover/tap breakdown popover - the demo twin
// of LeadScoreBadge. Stays inline (no portal) so it lives inside the card.
function StreamScoreBadge({ score, signals, rankPct }) {
  const [open, setOpen] = useState(false);
  const t = streamScoreTier(score);
  const total = signals.reduce((s, r) => s + r.count * r.weight, 0) || 1;
  const top = Math.max(...signals.map((r) => r.count * r.weight), 1);
  return (
    <div
      style={{ position: "relative", flexShrink: 0 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Lead score ${score} of 100, ${t.label}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "5px 11px", borderRadius: 999, cursor: "pointer",
          background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
          fontSize: 12, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap",
        }}
      >
        {score >= 70 && <Icon name="trendingUp" size={12} color={t.fg} />}
        <span style={{ opacity: 0.8, fontWeight: 600 }}>Score</span>
        <span style={{ fontSize: 14, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{score}</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            width: 270, zIndex: 30,
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
            boxShadow: "0 12px 34px rgba(0,0,0,0.16)", padding: "12px 13px",
            textAlign: "left", cursor: "default",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#111827" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.dot }} />
            Lead Score
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: "#6b7280" }}>
            <strong style={{ color: t.fg }}>{score}/100 · {t.label}</strong> · top {rankPct}% in your agency
          </div>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {signals.map((r) => {
              const raw = r.count * r.weight;
              return (
                <div key={r.label} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, fontSize: 11.5, color: "#374151" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ fontWeight: 600 }}>{r.label}</span>
                      <span style={{ color: "#9ca3af", marginLeft: 6, fontSize: 10.5 }}>{r.count} × {r.weight}pts</span>
                    </span>
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{raw}</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 999, background: "#f3f4f6", overflow: "hidden" }}>
                    <div style={{ width: `${Math.round((raw / top) * 100)}%`, height: "100%", background: t.dot }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#6b7280" }}>
            <span>Total points</span>
            <span style={{ fontVariantNumeric: "tabular-nums", color: "#374151", fontWeight: 700 }}>{total}</span>
          </div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#0d9488" }}>
            <Icon name="sparkles" size={12} color="#0d9488" />
            Tags applied automatically by your AI agent
          </div>
        </div>
      )}
    </div>
  );
}

function StreamContactRow({ c, isMobile }) {
  const t = streamScoreTier(c.score);
  // On mobile, cap visible tags so rows stay one tidy line instead of wrapping
  // into a crowded block; the rest collapse into a "+N" pill.
  const maxTags = isMobile ? 2 : c.tags.length;
  const visibleTags = c.tags.slice(0, maxTags);
  const hiddenCount = c.tags.length - visibleTags.length;
  return (
    <div
      className="vl-stream-row"
      style={{
        display: "flex",
        // Top-align on mobile so the score badge sits beside the name, not the
        // middle of the stacked block.
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? "0.7rem" : "0.9rem",
        padding: isMobile ? "0.8rem 0.9rem" : "0.85rem 1rem",
        borderBottom: "1px solid #f3f4f6", background: "#fff",
      }}
    >
      <div
        style={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700,
        }}
      >
        {streamInitials(c.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + suburb: inline on desktop, stacked on mobile so the name has
            room and never truncates to "Sar...". */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", minWidth: 0, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <span style={{ fontSize: 13.5, color: "#1f2937", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
          {!isMobile && <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>· {c.suburb}</span>}
        </div>
        {isMobile && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.suburb}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: 6, flexWrap: "wrap" }}>
          {visibleTags.map((tag) => (
            <span
              key={tag.label}
              title={`AI applied "${tag.label}"`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                padding: "2px 8px", borderRadius: 999,
                background: `${tag.color}1A`, color: tag.color,
                border: `1px solid ${tag.color}40`,
                fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
              }}
            >
              <Icon name="sparkles" size={9} color={tag.color} />
              {tag.label}
            </span>
          ))}
          {hiddenCount > 0 && (
            <span
              style={{
                display: "inline-flex", alignItems: "center",
                padding: "2px 8px", borderRadius: 999,
                background: "#f1f5f9", color: "#64748b",
                border: "1px solid #e2e8f0",
                fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
              }}
            >
              +{hiddenCount}
            </span>
          )}
        </div>
      </div>
      <StreamScoreBadge score={c.score} signals={c.signals} rankPct={c.rankPct} />
    </div>
  );
}

function StreamContacts({ isMobile }) {
  const contacts = useMemo(() => [...STREAM_CONTACTS].sort((a, b) => b.score - a.score), []);
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 10px 30px rgba(0,0,0,0.06)", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.7rem 1rem", borderBottom: "1px solid #f3f4f6", background: "#f8fafc" }}>
        <Icon name="sparkles" size={14} color={BRAND_ACCENT_COLOR} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#475569" }}>
          AI-scored &amp; tagged · sorted by who's ready to sell
        </span>
      </div>
      {contacts.map((c) => (
        <StreamContactRow key={c.id} c={c} isMobile={isMobile} />
      ))}
    </div>
  );
}

function LiveEventStream({ isMobile, defaultView = "list" }) {
  const [view, setView] = useState(defaultView); // "list" | "map" | "contacts"
  const [events, setEvents] = useState(() =>
    Array.from({ length: 5 }, () => {
      const e = makeStreamEvent();
      e.landedAt = Date.now() - Math.floor(Math.random() * 90000); // seed with history
      return e;
    }).sort((a, b) => b.landedAt - a.landedAt)
  );

  // Land a new event on a varying cadence.
  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return undefined;
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setEvents((prev) => [makeStreamEvent(), ...prev].slice(0, 6));
        schedule();
      }, 700 + Math.random() * 900);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  // Tick once a second so relative timestamps stay live.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto" }}>
      <style>{`
        @keyframes vlStreamIn {
          from { opacity: 0; transform: translateY(-14px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .vl-stream-row:first-child { animation: vlStreamIn 0.5s ease; }
        @keyframes vlPinDrop {
          0% { opacity: 0; transform: translate(-50%, -160%) scale(0.6); }
          60% { opacity: 1; transform: translate(-50%, -92%) scale(1.08); }
          100% { transform: translate(-50%, -100%) scale(1); }
        }
        .vl-map-pin-new { animation: vlPinDrop 0.5s cubic-bezier(0.22, 1, 0.36, 1); }
        @keyframes vlCalloutIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .vl-map-callout { animation: vlCalloutIn 0.4s ease; }
        @media (prefers-reduced-motion: reduce) {
          .vl-stream-row:first-child, .vl-map-pin-new, .vl-map-callout { animation: none; }
        }
      `}</style>
      {/* Header: live pill + Map / List tabs. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: "rgba(16,185,129,0.1)", color: BRAND_ACCENT_COLOR, fontSize: "0.78rem", fontWeight: 700, borderRadius: "999px", padding: "0.3rem 0.85rem", letterSpacing: "0.03em" }}>
          <span className="vl-pulse" style={{ "--pulse-rgba": "rgba(16,185,129,0.6)", width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} />
          LIVE · Lead signals landing now
        </span>
        <div style={{ display: "inline-flex", background: "#eef2f6", borderRadius: 999, padding: 3, border: "1px solid #e2e8f0" }}>
          {[
            { key: "map", icon: "mapPin", label: "Map" },
            { key: "list", icon: "activity", label: "List" },
            { key: "contacts", icon: "users", label: "Contacts" },
          ].map((t) => {
            const active = view === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: 999,
                  padding: "0.35rem 0.9rem",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  background: active ? "#fff" : "transparent",
                  color: active ? BRAND_COLOR : "#64748b",
                  boxShadow: active ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                <Icon name={t.icon} size={14} color={active ? BRAND_ACCENT_COLOR : "#94a3b8"} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {view === "map" ? (
        <StreamMap events={events} isMobile={isMobile} />
      ) : view === "contacts" ? (
        <StreamContacts isMobile={isMobile} />
      ) : (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {events.map((ev) => (
            <StreamRow key={ev.id} ev={ev} isMobile={isMobile} />
          ))}
        </div>
      )}

      <p style={{ textAlign: "center", color: "#94a3b8", fontSize: "0.8rem", marginTop: "0.9rem" }}>
        Every analysis, open, click and reply lands here automatically. Your warmest leads, surfacing themselves.
      </p>
    </div>
  );
}

// "You're still the boss": human-in-the-loop. A self-running, simplified take
// on the /needs-review queue - toggle between Email, SMS and Call notes and
// watch the agent draft each one. Send/Dismiss wait for you, so you can see
// "your agent drafts, you decide what sends" play out for every channel.
const APPROVAL_CHANNELS = {
  email: {
    label: "Email",
    tabIcon: "mail",
    chipIcon: "mail",
    chipLabel: "EMAIL",
    chip: { background: "#e0f2fe", color: "#075985" },
    kind: "REPLY",
    contact: "sarah.mitchell@example.com",
    contextLabel: "Their email",
    context:
      "Thanks for the appraisal! We're thinking of listing in the next month or two. Could you call to talk timing and fees?",
    subject: "Re: Your appraisal for 14 Darling Street",
    outputLabel: "Reply",
    output:
      "Hi Sarah,\n\nGreat to hear you're considering listing. Are you free for a quick call tomorrow morning? I'll bring a few recent Balmain sales so we can map out the best window together.\n\nWarm regards,\nOlivia",
    footnote: "Your agent's signature, photo and agency logo are appended automatically.",
    sendLabel: "Send",
    sendIcon: "send",
  },
  sms: {
    label: "SMS",
    tabIcon: "message",
    chipIcon: "message",
    chipLabel: "SMS",
    chip: { background: "#dcfce7", color: "#166534" },
    kind: "REPLY",
    contact: "0412 345 678",
    contextLabel: "Their text",
    context: "Hi, is 14 Darling St still open for inspection this weekend?",
    subject: "",
    outputLabel: "Message",
    output: "Hi Sarah! Yes, open Saturday 11 to 11:30am. Want me to pencil you in? Olivia",
    footnote: "Kept short and plain-text, sent from your agent's mobile number.",
    sendLabel: "Send",
    sendIcon: "send",
  },
  call: {
    label: "Call notes",
    tabIcon: "phone",
    chipIcon: "phone",
    chipLabel: "CALL",
    chip: { background: "#fef3c7", color: "#92400e" },
    kind: "OUTBOUND",
    contact: "0431 220 145",
    contextLabel: "Why call",
    context:
      "Hot seller. Opened your appraisal 3x this week and replied about timing. Best reached by phone.",
    subject: "",
    outputLabel: "Call note",
    output:
      "Talking points:\n• Thank Michael for the appraisal interest on 8 Smith St\n• Confirm timeline, looking to list in 4 to 6 weeks\n• Walk through fee + 3 recent local sales\n• Book an in-person market appraisal",
    footnote: "Saved as a handover note on the contact after you call.",
    sendLabel: "Log call",
    sendIcon: "draft",
  },
};
const APPROVAL_ORDER = ["email", "sms", "call"];

function ApprovalFlow({ isMobile }) {
  const [channel, setChannel] = useState("email");
  // incoming → opened → streaming → ready, then loop the same channel.
  const [phase, setPhase] = useState("incoming");
  const [typed, setTyped] = useState(0);
  const [cycle, setCycle] = useState(0);
  const ch = APPROVAL_CHANNELS[channel];
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Start (or restart) the sequence whenever the channel or loop changes.
  useEffect(() => {
    setPhase("incoming");
    setTyped(0);
    if (prefersReduced) {
      setPhase("ready");
      setTyped(ch.output.length);
      return undefined;
    }
    const timers = [
      setTimeout(() => setPhase("opened"), 1200),
      setTimeout(() => setPhase("streaming"), 2100),
    ];
    return () => timers.forEach(clearTimeout);
  }, [channel, cycle, ch.output.length, prefersReduced]);

  // Stream the draft into the box one character at a time.
  useEffect(() => {
    if (phase !== "streaming") return undefined;
    let i = 0;
    let timer;
    const tick = () => {
      i += 1;
      setTyped(i);
      if (i < ch.output.length) timer = setTimeout(tick, 18);
      else setPhase("ready");
    };
    timer = setTimeout(tick, 18);
    return () => clearTimeout(timer);
  }, [phase, ch.output]);

  // Hold on the finished draft, then replay the same channel.
  useEffect(() => {
    if (phase !== "ready") return undefined;
    const t = setTimeout(() => setCycle((c) => c + 1), prefersReduced ? 5000 : 3600);
    return () => clearTimeout(t);
  }, [phase, prefersReduced]);

  const streaming = phase === "streaming";
  const ready = phase === "ready";
  const showDraft = streaming || ready;
  const draftText = ch.output.slice(0, typed);
  const isReply = ch.kind === "REPLY";

  const pickChannel = (key) => {
    if (key === channel) return;
    setChannel(key);
    setCycle(0);
  };

  return (
    <div style={{ maxWidth: "620px", margin: "0 auto", height: isMobile ? 600 : 540, display: "flex", flexDirection: "column" }}>
      {/* Channel toggle - Email / SMS / Call notes. */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: "1rem" }}>
        {APPROVAL_ORDER.map((key) => {
          const c = APPROVAL_CHANNELS[key];
          const active = key === channel;
          return (
            <button
              key={key}
              type="button"
              onClick={() => pickChannel(key)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 999, cursor: "pointer",
                fontSize: 13, fontWeight: 700,
                border: `1px solid ${active ? "#7c3aed" : "#e2e8f0"}`,
                background: active ? "#7c3aed" : WHITE_COLOR,
                color: active ? "#fff" : "#64748b",
                transition: "background 0.15s, color 0.15s, border-color 0.15s",
              }}
            >
              <Icon name={c.tabIcon} size={13} color={active ? "#fff" : "#94a3b8"} />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* The open item - a stripped-back take on the Needs Review card. */}
      <div
        key={`${channel}-${cycle}`}
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: WHITE_COLOR,
          border: "1px solid #e5e7eb",
          borderRadius: "14px",
          boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
          overflow: "hidden",
          animation: prefersReduced ? "none" : "vlReplyIn 0.5s ease",
        }}
      >
        {/* Identity row: kind + channel chips and the contact. */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.85rem 1rem", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 10px", borderRadius: 999, fontWeight: 700, ...(isReply ? { background: "#dbeafe", color: "#1e40af" } : { background: "#ede9fe", color: "#6d28d9" }) }}>
            <Icon name={isReply ? "messageReply" : "send"} size={10} color={isReply ? "#1e40af" : "#6d28d9"} /> {isReply ? "REPLY" : "OUTBOUND"}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 10px", borderRadius: 999, fontWeight: 700, ...ch.chip }}>
            <Icon name={ch.chipIcon} size={10} color={ch.chip.color} /> {ch.chipLabel}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#111827", minWidth: 0 }}>
            <Icon name="cpu" size={13} color="#7c3aed" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ch.contact}</span>
          </span>
        </div>

        {/* Body fills the remaining height so the card stays a fixed size. */}
        <div style={{ padding: "1rem", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* The trigger: their message, or why we're calling. */}
          <div style={{ padding: "10px 12px", background: "#f9fafb", borderLeft: "3px solid #94a3b8", borderRadius: 4, fontSize: 13, color: "#374151", lineHeight: 1.5, marginBottom: "0.75rem" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
              {ch.contextLabel}
            </div>
            {ch.context}
          </div>

          {ch.subject && (
            <div style={{ fontSize: 12.5, color: "#374151", marginBottom: "0.6rem" }}>
              <span style={{ color: "#9ca3af" }}>Subject: </span>{ch.subject}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "2px 6px", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            <Icon name="draft" size={12} color="#7c3aed" style={{ alignSelf: "center", flexShrink: 0 }} />
            <span style={{ flexShrink: 0 }}>{ch.outputLabel}</span>
          </div>

          {/* Output box grows to fill, so streaming text never resizes the card. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${streaming ? "#c4b5fd" : "#d1d5db"}`,
              boxShadow: streaming ? "0 0 0 3px rgba(124,58,237,0.12)" : "none",
              fontSize: 14,
              lineHeight: 1.55,
              color: showDraft ? "#111827" : "#9ca3af",
              whiteSpace: "pre-wrap",
              background: WHITE_COLOR,
              boxSizing: "border-box",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            {showDraft ? (
              <>
                {draftText}
                {streaming && <span className="vl-type-cursor" style={{ background: "#7c3aed" }} />}
              </>
            ) : (
              `Olivia is preparing a ${ch.outputLabel.toLowerCase()}…`
            )}
          </div>

          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>{ch.footnote}</div>

          {/* Actions - you decide what sends. */}
          <div style={{ display: "flex", gap: 8, marginTop: "0.75rem", flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={!ready}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 18px", borderRadius: 8, border: "none",
                background: ready ? "linear-gradient(90deg, #7c3aed, #5b21b6)" : "#c4b5fd",
                color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: ready ? "pointer" : "default",
              }}
            >
              <Icon name={ch.sendIcon} size={12} color="#fff" />
              {ready ? ch.sendLabel : "Drafting…"}
            </button>
            <button
              type="button"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 18px", borderRadius: 8,
                border: "1px solid #d1d5db", background: "#fff",
                color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LOAD YOUR DATA: a step-by-step animation showing the two ways an agent gets
// your contacts - connect a CRM (Eagle, via client id + secret) or upload a
// spreadsheet that AI maps for you. Both finish with a little confetti.
// =============================================================================

const EAGLE_CLIENT_ID = "eagle_a1b2c3d4e5f6g7h8";
const EAGLE_CLIENT_SECRET = "sk_live_9f8e7d6c5b4a3f2e1d0c";

// Other CRMs we show greyed-out next to the highlighted Eagle tile.
const CRM_PROVIDERS = ["Eagle", "Rex", "VaultRE", "AgentBox", "LockedOn", "Box+Dice", "Pipedrive"];

// Spreadsheet columns and the contact fields AI matches them to.
const CSV_COLUMN_MAP = [
  { source: "First", field: "First name", confidence: 99 },
  { source: "Surname", field: "Last name", confidence: 99 },
  { source: "Email Address", field: "Email", confidence: 98 },
  { source: "Mobile No.", field: "Phone", confidence: 97 },
  { source: "Property Addr", field: "Address", confidence: 96 },
];

// A short burst of confetti, positioned absolutely over its parent card.
function Confetti() {
  const colors = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }).map((_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        dur: 1.6 + Math.random() * 1.3,
        size: 6 + Math.random() * 6,
        color: colors[i % colors.length],
        round: i % 3 === 0,
      })),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return (
    <div className="vl-confetti" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 6 }}>
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: -14,
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.45,
            background: p.color,
            borderRadius: p.round ? "50%" : 2,
            animation: `vlConfettiFall ${p.dur}s ${p.delay}s linear forwards`,
          }}
        />
      ))}
    </div>
  );
}

// Frosted scene wrapper so each animated step shares one fixed-size surface.
function Scene({ sceneKey, children }) {
  return (
    <div
      key={sceneKey}
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        animation: "vlReplyIn 0.45s ease",
      }}
    >
      {children}
    </div>
  );
}

function DataConnectFlow({ isMobile }) {
  const [tab, setTab] = useState("crm"); // "crm" | "csv"
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState(0); // chars revealed for the active credential
  const [rows, setRows] = useState(0); // CSV column-map rows revealed
  const [progress, setProgress] = useState(0); // 0-100 import progress
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const maxStep = tab === "crm" ? 5 : 4;

  // Auto-advance through the steps, then loop the same tab.
  useEffect(() => {
    if (prefersReduced) {
      setStep(maxStep);
      return undefined;
    }
    const durations =
      tab === "crm"
        ? [1300, 1500, 1500, 1300, 1700, 2800]
        : [1300, 1400, 1600, 2200, 2800];
    const t = setTimeout(() => setStep((s) => (s >= maxStep ? 0 : s + 1)), durations[step] || 2000);
    return () => clearTimeout(t);
  }, [tab, step, maxStep, prefersReduced]);

  // Type the client id (step 1) then the client secret (step 2).
  useEffect(() => {
    setTyped(0);
    if (prefersReduced) {
      setTyped(999);
      return undefined;
    }
    if (tab !== "crm" || (step !== 1 && step !== 2)) return undefined;
    const value = step === 1 ? EAGLE_CLIENT_ID : EAGLE_CLIENT_SECRET;
    let i = 0;
    let timer;
    const tick = () => {
      i += 1;
      setTyped(i);
      if (i < value.length) timer = setTimeout(tick, 45);
    };
    timer = setTimeout(tick, 250);
    return () => clearTimeout(timer);
  }, [tab, step, prefersReduced]);

  // Reveal the matched columns one row at a time during the CSV mapping step.
  useEffect(() => {
    setRows(0);
    if (tab !== "csv") return undefined;
    if (step > 3) {
      setRows(CSV_COLUMN_MAP.length);
      return undefined;
    }
    if (step !== 3) return undefined;
    if (prefersReduced) {
      setRows(CSV_COLUMN_MAP.length);
      return undefined;
    }
    let i = 0;
    let timer;
    const tick = () => {
      i += 1;
      setRows(i);
      if (i < CSV_COLUMN_MAP.length) timer = setTimeout(tick, 340);
    };
    timer = setTimeout(tick, 300);
    return () => clearTimeout(timer);
  }, [tab, step, prefersReduced]);

  // Fill the import progress bar (and contact counter) during the CRM import step.
  useEffect(() => {
    setProgress(0);
    if (!(tab === "crm" && step === 4)) {
      if (tab === "crm" && step === 5) setProgress(100);
      return undefined;
    }
    if (prefersReduced) {
      setProgress(100);
      return undefined;
    }
    let p = 0;
    let timer;
    const tick = () => {
      p = Math.min(100, p + 4 + Math.random() * 6);
      setProgress(p);
      if (p < 100) timer = setTimeout(tick, 70);
    };
    timer = setTimeout(tick, 120);
    return () => clearTimeout(timer);
  }, [tab, step, prefersReduced]);

  const pickTab = (key) => {
    if (key === tab) return;
    setTab(key);
    setStep(0);
  };

  const done = (tab === "crm" && step === 5) || (tab === "csv" && step === 4);

  const TABS = [
    { key: "crm", label: "Connect your CRM", icon: "database" },
    { key: "csv", label: "Upload a spreadsheet", icon: "spreadsheet" },
  ];

  return (
    <div style={{ maxWidth: "620px", margin: "0 auto", width: "100%", height: isMobile ? 540 : 500, display: "flex", flexDirection: "column" }}>
      {/* Source toggle - CRM vs spreadsheet. */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: "1rem" }}>
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => pickTab(t.key)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "8px 16px", borderRadius: 999, cursor: "pointer",
                fontSize: 13, fontWeight: 700,
                border: `1px solid ${active ? "#10b981" : "#e2e8f0"}`,
                background: active ? "#10b981" : WHITE_COLOR,
                color: active ? "#fff" : "#64748b",
                transition: "background 0.15s, color 0.15s, border-color 0.15s",
              }}
            >
              <Icon name={t.icon} size={14} color={active ? "#fff" : "#94a3b8"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* The surface. Confetti is absolutely positioned over it. */}
      <div
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: WHITE_COLOR,
          border: "1px solid #e5e7eb",
          borderRadius: "14px",
          boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
          overflow: "hidden",
        }}
      >
        {done && <Confetti />}

        {/* Window chrome / title bar. */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.85rem 1rem", borderBottom: "1px solid #f1f5f9" }}>
          <Icon name={tab === "crm" ? "link" : "upload"} size={16} color="#10b981" />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "#111827" }}>
            {tab === "crm" ? "Connect your CRM" : "Upload your contacts"}
          </span>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#6d28d9", background: "#ede9fe", padding: "3px 9px", borderRadius: 999 }}>
            <Icon name="sparkles" size={10} color="#6d28d9" /> AI-assisted
          </span>
        </div>

        {/* Body. */}
        <div style={{ padding: "1.1rem", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {tab === "crm" ? (
            <CrmScenes step={step} typed={typed} progress={progress} />
          ) : (
            <CsvScenes step={step} rows={rows} />
          )}
        </div>

        {/* Step dots. */}
        <div style={{ display: "flex", justifyContent: "center", gap: 6, padding: "0 0 0.9rem" }}>
          {Array.from({ length: maxStep + 1 }).map((_, i) => (
            <span
              key={i}
              style={{
                width: i === step ? 18 : 6,
                height: 6,
                borderRadius: 999,
                background: i <= step ? "#10b981" : "#e2e8f0",
                transition: "width 0.25s ease, background 0.25s ease",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const CRM_TOTAL_CONTACTS = 2847;

// CRM path: pick Eagle, paste client id + secret, connect, import, done.
function CrmScenes({ step, typed, progress }) {
  if (step === 0) {
    return (
      <Scene sceneKey="crm-pick">
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: "0.9rem" }}>
          Choose where your contacts live
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {CRM_PROVIDERS.map((name) => {
            const isEagle = name === "Eagle";
            return (
              <div
                key={name}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 14px", borderRadius: 10,
                  border: `1.5px solid ${isEagle ? "#10b981" : "#e5e7eb"}`,
                  background: isEagle ? "rgba(16,185,129,0.06)" : WHITE_COLOR,
                  boxShadow: isEagle ? "0 0 0 3px rgba(16,185,129,0.12)" : "none",
                  opacity: isEagle ? 1 : 0.55,
                }}
              >
                <div style={{ width: 30, height: 30, borderRadius: 8, background: isEagle ? "#10b981" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="database" size={15} color={isEagle ? "#fff" : "#94a3b8"} />
                </div>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: isEagle ? "#065f46" : "#9ca3af" }}>{name}</span>
                {isEagle && <Icon name="checkCircle" size={16} color="#10b981" style={{ marginLeft: "auto" }} />}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#9ca3af" }}>
          <Icon name="loader" size={12} color="#9ca3af" className="vl-spin" /> Selecting Eagle…
        </div>
      </Scene>
    );
  }

  if (step === 1 || step === 2 || step === 3) {
    const idText = step === 1 ? EAGLE_CLIENT_ID.slice(0, typed) : EAGLE_CLIENT_ID;
    const secretText = step === 1 ? "" : step === 2 ? EAGLE_CLIENT_SECRET.slice(0, typed) : EAGLE_CLIENT_SECRET;
    const connecting = step === 3;
    return (
      <Scene sceneKey="crm-creds">
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: "0.9rem" }}>
          <Icon name="key" size={15} color="#10b981" /> Eagle API credentials
        </div>

        <CredField label="Client ID" value={idText} active={step === 1} icon="user" />
        <div style={{ height: 12 }} />
        <CredField label="Client Secret" value={secretText} active={step === 2} icon="lock" placeholder="Paste your secret…" />

        <button
          type="button"
          disabled={!connecting}
          style={{
            marginTop: "1rem",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            padding: "11px 18px", borderRadius: 9, border: "none",
            background: connecting ? "#10b981" : step >= 2 ? "#10b981" : "#a7f3d0",
            color: "#fff", fontWeight: 700, fontSize: 13.5,
            cursor: "default", alignSelf: "stretch",
          }}
        >
          {connecting ? (
            <>
              <Icon name="loader" size={15} color="#fff" className="vl-spin" /> Connecting to Eagle…
            </>
          ) : (
            <>
              <Icon name="link" size={15} color="#fff" /> Connect
            </>
          )}
        </button>
        <div style={{ marginTop: "auto", paddingTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#9ca3af" }}>
          <Icon name="shield" size={13} color="#94a3b8" /> Credentials are encrypted and never shared.
        </div>
      </Scene>
    );
  }

  if (step === 4) {
    const loaded = Math.round((progress / 100) * CRM_TOTAL_CONTACTS);
    return (
      <Scene sceneKey="crm-import">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 14, textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="loader" size={26} color="#10b981" className="vl-spin" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Importing your contacts</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Loading {loaded.toLocaleString()} of {CRM_TOTAL_CONTACTS.toLocaleString()} contacts from Eagle…
          </div>
          <div style={{ width: "78%", maxWidth: 340 }}>
            <div style={{ height: 9, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progress}%`, background: ACCENT_GRADIENT, borderRadius: 999, transition: "width 0.15s linear" }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "#059669" }}>{Math.round(progress)}%</div>
          </div>
        </div>
      </Scene>
    );
  }

  // step 5 - done
  return (
    <Scene sceneKey="crm-done">
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 12, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(16,185,129,0.12)", display: "flex", alignItems: "center", justifyContent: "center", animation: "vlPopIn 0.5s ease" }}>
          <Icon name="checkCircle" size={32} color="#10b981" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#065f46" }}>Eagle connected</div>
        <div style={{ fontSize: 13.5, color: "#6b7280", maxWidth: 320, lineHeight: 1.5 }}>
          2,847 contacts synced. Your AI agent can now reach every one of them.
        </div>
      </div>
    </Scene>
  );
}

// A single credential input, with a typing cursor while it fills in.
function CredField({ label, value, active, icon, placeholder = "" }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>{label}</div>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 9,
          padding: "11px 12px", borderRadius: 9,
          border: `1px solid ${active ? "#10b981" : "#d1d5db"}`,
          boxShadow: active ? "0 0 0 3px rgba(16,185,129,0.12)" : "none",
          background: WHITE_COLOR,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <Icon name={icon} size={15} color={active ? "#10b981" : "#9ca3af"} />
        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, color: value ? "#111827" : "#9ca3af", wordBreak: "break-all" }}>
          {value || placeholder}
          {active && <span className="vl-type-cursor" />}
        </span>
      </div>
    </div>
  );
}

// Spreadsheet path: drop a file, AI scans it, columns get matched, done.
function CsvScenes({ step, rows }) {
  if (step === 0) {
    return (
      <Scene sceneKey="csv-drop">
        <div
          style={{
            flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 12,
            border: "2px dashed #cbd5e1", borderRadius: 12, background: "#f8fafc", textAlign: "center", padding: "1rem",
          }}
        >
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center", animation: "vlPulse 2s infinite" }}>
            <Icon name="upload" size={24} color="#10b981" />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>Drag &amp; drop your contacts</div>
          <div style={{ fontSize: 12.5, color: "#9ca3af" }}>CSV or Excel, any columns, any order</div>
        </div>
      </Scene>
    );
  }

  if (step === 1 || step === 2) {
    const scanning = step === 2;
    return (
      <Scene sceneKey="csv-file">
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px", borderRadius: 11, border: "1px solid #e5e7eb", background: "#f9fafb", animation: "vlPopIn 0.4s ease" }}>
          <div style={{ width: 38, height: 38, borderRadius: 9, background: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="spreadsheet" size={19} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#111827" }}>contacts.xlsx</div>
            <div style={{ fontSize: 11.5, color: "#9ca3af" }}>2,847 rows · 8 columns</div>
          </div>
          <Icon name="checkCircle" size={18} color="#10b981" style={{ marginLeft: "auto" }} />
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 12, textAlign: "center" }}>
          {scanning ? (
            <>
              <Icon name="loader" size={26} color="#7c3aed" className="vl-spin" />
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "#6d28d9" }}>AI is reading your columns…</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Figuring out which column is which</div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#9ca3af" }}>
              <Icon name="loader" size={14} color="#9ca3af" className="vl-spin" /> Uploaded. Preparing to read it…
            </div>
          )}
        </div>
      </Scene>
    );
  }

  // step 3 (mapping reveal) and step 4 (done) share the mapping list.
  const matching = step === 3 && rows < CSV_COLUMN_MAP.length;
  return (
    <Scene sceneKey="csv-map">
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 700, color: "#6d28d9", marginBottom: "0.7rem" }}>
        {matching ? (
          <>
            <Icon name="loader" size={14} color="#6d28d9" className="vl-spin" /> AI is matching your columns…
          </>
        ) : (
          <>
            <Icon name="sparkles" size={14} color="#6d28d9" /> AI matched your columns
          </>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {CSV_COLUMN_MAP.map((m, i) => {
          const shown = i < rows;
          return (
            <div
              key={m.source}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8,
                border: "1px solid #f1f5f9", background: "#fbfdfc",
                opacity: shown ? 1 : 0,
                transform: shown ? "translateY(0)" : "translateY(6px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
            >
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#64748b", background: "#f1f5f9", padding: "3px 8px", borderRadius: 5, whiteSpace: "nowrap" }}>{m.source}</span>
              <Icon name="arrowRight" size={13} color="#cbd5e1" />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{m.field}</span>
              <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: "#059669" }}>
                <Icon name="checkCircle" size={12} color="#10b981" /> {m.confidence}%
              </span>
            </div>
          );
        })}
      </div>
      {step === 4 && (
        <div style={{ marginTop: "auto", paddingTop: 12, display: "flex", alignItems: "center", gap: 9, animation: "vlPopIn 0.5s ease" }}>
          <Icon name="checkCircle" size={20} color="#10b981" />
          <span style={{ fontSize: 13.5, fontWeight: 800, color: "#065f46" }}>All set. 2,847 contacts ready for your agent.</span>
        </div>
      )}
    </Scene>
  );
}

// Small reusable eyebrow chip.
function Eyebrow({ children }) {
  return (
    <div
      style={{
        display: "inline-block",
        background: "rgba(16,185,129,0.1)",
        color: BRAND_ACCENT_COLOR,
        fontSize: "0.78rem",
        fontWeight: 700,
        borderRadius: "6px",
        padding: "0.3rem 0.75rem",
        marginBottom: "1rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

// Hero headline, split so the gradient highlight names the real-estate focus.
// The lead + highlight + connector stay fixed; the closing phrase rotates,
// retyping itself after deleting the previous one (see useRotatingTypewriter).
const HERO_LEAD = "Hire an ";
const HERO_HIGHLIGHT = "AI agent";
const HERO_CONNECT = " who ";
const HERO_PREFIX = HERO_LEAD + HERO_HIGHLIGHT + HERO_CONNECT; // "Hire an AI agent who "
const HERO_LEAD_LEN = HERO_LEAD.length;
const HERO_HL_END = HERO_LEAD.length + HERO_HIGHLIGHT.length;
const HERO_PREFIX_LEN = HERO_PREFIX.length;
const HERO_PHRASES = [
  "captures homeowner leads while you sleep", // DIY valuator + QR flyers
  "writes market articles while you sleep", // AI articles
  "emails property reports while you sleep", // monthly property reports
  "texts property updates while you sleep", // AI SMS campaigns
  "drafts your replies while you sleep", // two-way AI reply drafting
  "ranks your contacts while you sleep", // lead scoring & tagging
  "spots the hot sellers while you sleep", // seller signals / map prospector
  "builds appraisals while you sleep", // premium AI appraisals
];
// The longest phrase, used to reserve the phrase line's height so the copy
// below never jumps as phrases of different lengths type and delete.
const HERO_LONGEST_PHRASE = HERO_PHRASES.reduce((a, b) => (b.length > a.length ? b : a), "");
const HERO_GRADIENT = {
  background: "linear-gradient(90deg, #10b981, #3b82f6)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

// Reveals `text` one character at a time, like an AI typing it out. Honors
// prefers-reduced-motion by showing the full text immediately.
function useTypewriter(text, { startDelay = 500, speed = 45 } = {}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setCount(text.length);
      return undefined;
    }
    setCount(0);
    let i = 0;
    let typeTimer;
    const startTimer = setTimeout(function tick() {
      i += 1;
      setCount(i);
      if (i < text.length) typeTimer = setTimeout(tick, speed);
    }, startDelay);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(typeTimer);
    };
  }, [text, startDelay, speed]);
  return count;
}

// Types `prefix` once, then cycles through `phrases`: type a phrase, hold, then
// delete it back to the prefix (the prefix is never erased) and type the next.
// Returns the full string currently visible, including the prefix. Honors
// prefers-reduced-motion by settling on the first phrase.
function useRotatingTypewriter(
  prefix,
  phrases,
  { startDelay = 500, typeSpeed = 55, deleteSpeed = 28, holdTime = 1900 } = {}
) {
  const [text, setText] = useState("");
  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setText(prefix + phrases[0]);
      return undefined;
    }
    let cancelled = false;
    let timer;
    let phraseIdx = 0;
    let count = 0; // characters of (prefix + current phrase) currently shown
    let mode = "typing"; // "typing" | "holding" | "deleting"

    const step = () => {
      if (cancelled) return;
      const target = prefix + phrases[phraseIdx];
      if (mode === "typing") {
        count += 1;
        setText(target.slice(0, count));
        if (count >= target.length) {
          mode = "holding";
          timer = setTimeout(step, holdTime);
        } else {
          timer = setTimeout(step, typeSpeed);
        }
      } else if (mode === "holding") {
        mode = "deleting";
        timer = setTimeout(step, deleteSpeed);
      } else {
        count -= 1;
        setText(target.slice(0, Math.max(count, prefix.length)));
        if (count <= prefix.length) {
          phraseIdx = (phraseIdx + 1) % phrases.length;
          count = prefix.length;
          mode = "typing";
          timer = setTimeout(step, typeSpeed);
        } else {
          timer = setTimeout(step, deleteSpeed);
        }
      }
    };

    setText("");
    timer = setTimeout(step, startDelay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [prefix, phrases, startDelay, typeSpeed, deleteSpeed, holdTime]);
  return text;
}

// Holds a self-running demo inert until it scrolls into view, then mounts it so
// its entrance animations and timers start exactly when the viewer reaches it.
// `minHeight` reserves the demo's footprint so revealing it doesn't shift layout.
function RevealOnScroll({ minHeight, children }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    // No observer support (or SSR): just render it.
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  return (
    <div ref={ref} style={{ minHeight: shown ? undefined : minHeight }}>
      {shown ? children : null}
    </div>
  );
}

export default function LandingPage() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const heroText = useRotatingTypewriter(HERO_PREFIX, HERO_PHRASES);
  const agencyBranding = useAgencyBranding();

  // The referral banner (and anywhere else outside this component) opens the
  // sign-in modal by dispatching a `vl-open-signin` event on window — saves
  // having to thread setShowSignIn through MarketingLayout's <Outlet />.
  useEffect(() => {
    const openSignIn = () => setShowSignIn(true);
    window.addEventListener("vl-open-signin", openSignIn);
    return () => window.removeEventListener("vl-open-signin", openSignIn);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    el && el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", background: WHITE_COLOR }}>
      <style>{`
        @keyframes vlTypeBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
        .vl-type-cursor {
          display: inline-block;
          width: 0.06em;
          min-width: 2px;
          height: 1em;
          margin-left: 0.06em;
          vertical-align: text-bottom;
          background: #10b981;
          border-radius: 1px;
          animation: vlTypeBlink 1s steps(1, end) infinite;
        }
        @media (prefers-reduced-motion: reduce) { .vl-type-cursor { animation: none; } }
        @keyframes vlPulse {
          0% { box-shadow: 0 0 0 0 var(--pulse-rgba, rgba(16,185,129,0.5)); }
          70% { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
        }
        .vl-pulse { animation: vlPulse 2s infinite; }
        .vl-pulse:hover { transform: translateY(-1px); }
        @media (prefers-reduced-motion: reduce) { .vl-pulse { animation: none; } }
        @keyframes vlReplyIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) { @keyframes vlReplyIn { from { opacity: 1; } to { opacity: 1; } } }
        @keyframes vlStepFade {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vlPopIn {
          0% { transform: scale(0.9); opacity: 0; }
          60% { transform: scale(1.03); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes vlConfettiFall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(440px) rotate(620deg); opacity: 0; }
        }
        @keyframes vlSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .vl-spin { animation: vlSpin 0.9s linear infinite; transform-origin: center; }
        @media (prefers-reduced-motion: reduce) {
          .vl-confetti { display: none !important; }
          .vl-spin { animation: none; }
        }
      `}</style>
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* ================= HERO ================= */}
      <header style={{ background: DARK_GRADIENT, padding: isMobile ? "3.5rem 1.5rem" : "5.5rem 2rem", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: GRID_OVERLAY, backgroundSize: "60px 60px", pointerEvents: "none" }} />
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            gap: isMobile ? "2.5rem" : "3.5rem",
          }}
        >
          {/* Left: promise */}
          <div style={{ flex: "1 1 55%", textAlign: isMobile ? "center" : "left" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: "rgba(16, 185, 129, 0.15)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                color: BRAND_ACCENT_COLOR,
                fontSize: "0.85rem",
                fontWeight: 600,
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                marginBottom: "1.75rem",
                letterSpacing: "0.03em",
              }}
            >
              <Icon name="cpu" size={15} color={BRAND_ACCENT_COLOR} /> Your 24/7 AI prospecting agent
            </div>
            <h1
              aria-label={HERO_PREFIX + HERO_PHRASES[0]}
              style={{
                fontSize: isMobile ? "2.2rem" : "3.3rem",
                fontWeight: 800,
                marginBottom: "1.5rem",
                lineHeight: 1.12,
                color: WHITE_COLOR,
                letterSpacing: "-0.02em",
                position: "relative",
              }}
            >
              {/* Ghost: reserves the prefix line plus the longest phrase line so
                  the copy below never jumps as phrases type and delete. Mirrors
                  the live layout exactly. On mobile the "who" connector lives on
                  the phrase line so the centred text never strands it alone. */}
              <span aria-hidden="true" style={{ visibility: "hidden" }}>
                {HERO_LEAD}
                <span style={HERO_GRADIENT}>{HERO_HIGHLIGHT}</span>
                {!isMobile && HERO_CONNECT}
                <span style={{ display: "block" }}>
                  {(isMobile ? HERO_CONNECT : "") + HERO_LONGEST_PHRASE}
                </span>
              </span>
              {/* Typed overlay: prefix stays put, the closing phrase rotates on
                  its own line so deleting it never reflows words onto the line
                  above. On mobile the "who" connector moves onto the phrase line
                  (it is never deleted — only the phrase after it is). */}
              <span aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
                {heroText.slice(0, Math.min(heroText.length, HERO_LEAD_LEN))}
                <span style={HERO_GRADIENT}>
                  {heroText.length > HERO_LEAD_LEN
                    ? heroText.slice(HERO_LEAD_LEN, Math.min(heroText.length, HERO_HL_END))
                    : ""}
                </span>
                {!isMobile && heroText.length > HERO_HL_END
                  ? heroText.slice(HERO_HL_END, Math.min(heroText.length, HERO_PREFIX_LEN))
                  : ""}
                <span style={{ display: "block" }}>
                  {isMobile && heroText.length > HERO_HL_END
                    ? heroText.slice(HERO_HL_END, Math.min(heroText.length, HERO_PREFIX_LEN))
                    : ""}
                  {heroText.length > HERO_PREFIX_LEN ? heroText.slice(HERO_PREFIX_LEN) : ""}
                  <span className="vl-type-cursor" />
                </span>
              </span>
            </h1>
            <p
              style={{
                fontSize: isMobile ? "1.05rem" : "1.2rem",
                color: "rgba(255,255,255,0.7)",
                marginBottom: "2.25rem",
                maxWidth: "560px",
                marginLeft: isMobile ? "auto" : 0,
                marginRight: isMobile ? "auto" : 0,
                lineHeight: 1.7,
              }}
            >
              Built for real estate, they capture leads, nurture every contact under your brand, and tell you exactly who's ready to sell, all from a real inbox on your own domain. You approve; they do the rest.
            </p>
            <div style={{ display: "flex", justifyContent: isMobile ? "center" : "flex-start", gap: "0.75rem", flexDirection: isMobile ? "column" : "row", alignItems: "center", maxWidth: isMobile ? "320px" : "none", margin: isMobile ? "0 auto" : undefined }}>
              <button
                style={{
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.85rem 2rem",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: BRAND_ACCENT_COLOR,
                  color: WHITE_COLOR,
                  boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  minWidth: "200px",
                  width: isMobile ? "100%" : "auto",
                }}
                onClick={() => setShowSignIn(true)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
                }}
              >
                Hire your agent
              </button>
              <button
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: "8px",
                  padding: "0.85rem 2rem",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.08)",
                  color: WHITE_COLOR,
                  backdropFilter: "blur(4px)",
                  transition: "background 0.15s",
                  minWidth: "200px",
                  width: isMobile ? "100%" : "auto",
                }}
                onClick={() => scrollTo("features")}
                onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
                onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
              >
                See what they do ↓
              </button>
            </div>
          </div>

          {/* Right: the live map of lead signals landing in real time */}
          <div style={{ flex: "1 1 45%", width: "100%" }}>
            <HeroSignalMap isMobile={isMobile} />
            <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.78rem", marginTop: "1rem" }}>
              Every analysis, open, click & reply lands here automatically. Your warmest leads, surfacing themselves.
            </p>
          </div>
        </div>
      </header>

      {/* ================= TRUST STRIP ================= */}
      <section style={{ background: LIGHT_BG, borderBottom: "1px solid #e2e8f0", padding: "1.5rem 1rem" }}>
        <div
          style={{
            maxWidth: "950px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, auto)",
            gap: isMobile ? "1rem 2rem" : "3rem",
            justifyContent: "center",
            justifyItems: "center",
            textAlign: "center",
          }}
        >
          {[
            { num: "Works 24/7", label: "Never takes leave" },
            { num: "Your Brand", label: "On every touchpoint" },
            { num: "You Approve", label: "Always in control" },
            { num: "10-Min Setup", label: "Then it runs itself" },
          ].map((stat) => (
            <div key={stat.label} style={{ minWidth: "100px" }}>
              <div style={{ fontSize: "1.4rem", fontWeight: 800, color: BRAND_COLOR }}>{stat.num}</div>
              <div style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500, marginTop: "0.15rem" }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= MEET YOUR AGENT ================= */}
      <section style={{ background: WHITE_COLOR, padding: isMobile ? "3.5rem 1.5rem" : "5.5rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              gap: isMobile ? "2.5rem" : "4rem",
            }}
          >
            <div style={{ flex: "1 1 50%", maxWidth: isMobile ? "100%" : "540px", width: "100%" }}>
              <RevealOnScroll minHeight={540}>
                <OnboardingFlow isMobile={isMobile} branding={agencyBranding} />
              </RevealOnScroll>
            </div>
            <div style={{ flex: "1 1 50%", maxWidth: isMobile ? "100%" : "480px" }}>
              <Eyebrow>Meet your agent</Eyebrow>
              <h2 style={{ fontSize: isMobile ? "1.7rem" : "2.2rem", fontWeight: 700, color: BRAND_COLOR, marginBottom: "1rem", lineHeight: 1.2, letterSpacing: "-0.01em" }}>
                A real associate with a face, name, inbox and phone
              </h2>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {[
                  "Custom photo, name, title and signature, set once",
                  "A real inbox on your own domain, plus a dedicated phone number, managed for you",
                  "Hyper-personalised plain-text emails that never look automated",
                  "Replies to inbound conversations and works alongside you 24/7",
                ].map((b) => (
                  <li key={b} style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", fontSize: "0.95rem", color: GRAY_DARK_COLOR, lineHeight: 1.5 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "50%", background: "rgba(16,185,129,0.12)", color: BRAND_ACCENT_COLOR, fontSize: "0.7rem", flexShrink: 0, marginTop: "2px" }}>✓</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <TestimonialsShowcase isMobile={isMobile} featured="ashley" />

      {/* ================= THE THREE JOBS ================= */}
      <JobTabs jobs={JOBS} isMobile={isMobile} />
      <div id="features">
        {JOBS.map((job, idx) => (
          <section
            key={job.num}
            id={`job-${job.num}`}
            style={{
              background: job.background,
              padding: isMobile ? "2.5rem 1.5rem 3.5rem" : "3rem 2rem 5rem",
              borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
              // Clear the sticky navbar + floating tabs when scrolled to via a tab.
              scrollMarginTop: isMobile ? 128 : 138,
            }}
          >
            <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
              {/* Chapter header */}
              <div style={{ textAlign: "center", maxWidth: "640px", margin: "0 auto 2.5rem" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                  <span
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      background: ACCENT_GRADIENT,
                      color: WHITE_COLOR,
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {job.num}
                  </span>
                  <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 700, fontSize: "0.95rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {job.eyebrow}
                  </span>
                </div>
                <h3 style={{ fontSize: isMobile ? "1.55rem" : "2rem", fontWeight: 700, color: BRAND_COLOR, margin: "0 0 0.75rem 0", lineHeight: 1.25, letterSpacing: "-0.01em" }}>
                  {job.title}
                </h3>
                {job.subtitle && (
                  <p style={{ fontSize: "1rem", color: GRAY_DARK_COLOR, lineHeight: 1.65, margin: 0 }}>
                    {job.subtitle}
                  </p>
                )}
                {job.num === "2" && agencyBranding && agencyBranding.ready && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      margin: "1.5rem auto 0",
                      maxWidth: "440px",
                      textAlign: "left",
                      background: "rgba(16,185,129,0.08)",
                      border: "1px solid rgba(16,185,129,0.25)",
                      borderRadius: "12px",
                      padding: "0.85rem 1.1rem",
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: "rgba(16,185,129,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon name="send" size={15} color={BRAND_ACCENT_COLOR} />
                    </span>
                    <span style={{ fontSize: "0.9rem", color: GRAY_DARK_COLOR, lineHeight: 1.45 }}>
                      <strong style={{ color: BRAND_ACCENT_COLOR, fontWeight: 700 }}>Try it now:</strong>{" "}
                      drop your email or phone into any card below and we'll send you a sample, branded as you.
                    </span>
                  </div>
                )}
              </div>

              {/* Feature cards (Job 3 swaps these for the live feed below). */}
              {job.num !== "3" && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", justifyContent: "center" }}>
                  {job.features.map((feature) => (
                    <FeatureCard key={feature.title} feature={feature} accent={BRAND_ACCENT_COLOR} isMobile={isMobile} branding={agencyBranding} />
                  ))}
                </div>
              )}

              {/* Job 3: a live, self-running feed of seller-intent events, as a
                  Map / List toggle in place of the old feature cards. */}
              {job.num === "3" && (
                <RevealOnScroll minHeight={isMobile ? 560 : 520}>
                  <LiveEventStream isMobile={isMobile} />
                </RevealOnScroll>
              )}
            </div>
          </section>
        ))}
      </div>

      {/* ================= YOU'RE STILL THE BOSS ================= */}
      <section style={{ background: LIGHT_BG, padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", maxWidth: "640px", margin: "0 auto 2.5rem" }}>
            <Eyebrow>You're still the boss</Eyebrow>
            <h2 style={{ fontSize: isMobile ? "1.8rem" : "2.4rem", fontWeight: 800, color: BRAND_COLOR, margin: 0, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
              Your agent drafts. You decide what sends.
            </h2>
          </div>

          <RevealOnScroll minHeight={isMobile ? 600 : 540}>
            <ApprovalFlow isMobile={isMobile} />
          </RevealOnScroll>
        </div>
      </section>

      {/* ================= ONBOARD YOUR AGENT ================= */}
      <section
        id="howitworks"
        style={{ background: DARK_GRADIENT, padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem", position: "relative", overflow: "hidden" }}
      >
        <div style={{ position: "absolute", inset: 0, backgroundImage: GRID_OVERLAY, backgroundSize: "60px 60px", pointerEvents: "none" }} />
        <div style={{ maxWidth: "1000px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h2 style={{ textAlign: "center", fontSize: isMobile ? "1.8rem" : "2.4rem", fontWeight: 800, marginBottom: "0.75rem", color: WHITE_COLOR, letterSpacing: "-0.02em" }}>
            Load your contacts in minutes
          </h2>
          <p style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: "1.05rem", lineHeight: 1.6, maxWidth: "620px", margin: "0 auto 2.75rem" }}>
            Connect your CRM or upload a spreadsheet. AI does the rest, cleaning the data and matching every column, so your agent is ready to work.
          </p>
          <RevealOnScroll minHeight={isMobile ? 540 : 500}>
            <DataConnectFlow isMobile={isMobile} />
          </RevealOnScroll>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <FAQ
        isMobile={isMobile}
        items={[
          {
            question: "How long does it take to get my agent set up?",
            answer: "About 10 minutes. Upload your contacts, give your agent a name, face and branding, choose your suburbs, and they start working immediately. From there, it's completely set and forget.",
          },
          {
            question: "How much ongoing work is required from me?",
            answer: "Almost none. Once your agent is set up, they generate and send property reports, publish articles, run SMS campaigns, and nurture leads autonomously every month. You only step in to approve AI-drafted replies when a contact responds.",
          },
          {
            question: "Does the AI agent really get its own email address and phone number?",
            answer: "Yes. Your agent gets a real inbox on your own custom domain, plus a dedicated phone number, both of which we set up and manage for you. They send hyper-personalised, plain-text emails, reply to inbound conversations and run SMS campaigns from their own number, so to your contacts, they feel like a genuine member of your team.",
          },
          {
            question: "What happens when a contact replies to my agent?",
            answer: "Replies land in your dashboard where your agent drafts a suggested response. You can accept it with one click or write your own. Nothing is ever sent without your approval, so you always stay in control of the conversation.",
          },
          {
            question: "Is everything branded as my agency?",
            answer: "Absolutely. Every touchpoint carries your profile photo, agency logo, brand colours and contact details. Your clients see your brand front and centre on every report, article, email and message.",
          },
          {
            question: "Is there a lock-in contract?",
            answer: "No. Vendorleads is month-to-month with no lock-in contracts. You can upgrade, downgrade, or cancel your plan at any time from your account settings.",
          },
        ]}
      />

      {/* ================= CTA ================= */}
      <section style={{ background: WHITE_COLOR, padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem", textAlign: "center" }}>
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? "1.8rem" : "2.4rem", fontWeight: 800, color: BRAND_COLOR, marginBottom: "1rem", lineHeight: 1.2, letterSpacing: "-0.02em" }}>
            Ready to hire your AI agent?
          </h2>
          <p style={{ color: GRAY_DARK_COLOR, marginBottom: "2rem", fontSize: "1.1rem", lineHeight: 1.6 }}>
            Set them up in 10 minutes. From there, they capture leads, nurture every contact, and surface the sellers, so you can focus on closing.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", flexDirection: isMobile ? "column" : "row", alignItems: "center", maxWidth: isMobile ? "320px" : "none", margin: isMobile ? "0 auto" : undefined }}>
            <button
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "0.9rem 2.5rem",
                fontSize: "1.05rem",
                fontWeight: 600,
                cursor: "pointer",
                background: BRAND_ACCENT_COLOR,
                color: WHITE_COLOR,
                boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                transition: "transform 0.15s, box-shadow 0.15s",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={() => setShowSignIn(true)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
              }}
            >
              Get Started
            </button>
            <button
              style={{
                border: `1px solid ${BRAND_ACCENT_COLOR}`,
                borderRadius: "8px",
                padding: "0.9rem 2.5rem",
                fontSize: "1.05rem",
                fontWeight: 600,
                cursor: "pointer",
                background: "transparent",
                color: BRAND_ACCENT_COLOR,
                transition: "background 0.15s, color 0.15s",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={() => setShowContact(true)}
              onMouseOver={(e) => {
                e.currentTarget.style.background = BRAND_ACCENT_COLOR;
                e.currentTarget.style.color = WHITE_COLOR;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = BRAND_ACCENT_COLOR;
              }}
            >
              Talk to us
            </button>
          </div>
        </div>
      </section>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
      {showContact && <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />}
    </div>
  );
}
